import type { EmailDiagnostic, EmailLifecycleTransition } from "@shared/emailDiagnostics";
import { trackIntakeFactSave } from "./intakeFactSaves";
import type { DashboardScheduleItem } from "@shared/dashboardSchedule";
import type { AiVideoJobPayload, Task, Lead, Goal, GoalMetricConfig, GoalStep, Source, TaskFolder, ClassScheduleEntry, PipelineStage, Direction, Channel, Relationship, EmailHistory, AiVideoJob, BusinessSlide, InsertBusinessSlide, WebsiteTab, InsertWebsiteTab, LegalSection, SeoMeta, CheckinNotificationEmailOptions } from "@shared/schema";
import type { ReactivationGoalKey, ReactivationField } from "@shared/reactivationCampaigns";
import type { OutreachMasterKey, OutreachField } from "@shared/outreachMasters";
import type { BusinessHours } from "@shared/businessHours";
import type { CampaignConsultLibrary, TemplateConsultLibrary, ConsultKbFaq } from "@shared/consultLibrary";
import type { AnswerGap } from "@shared/receptionistAnswerGaps";
import type { ReferralFlowSettings, ReferralFlowPatch } from "@shared/referralFlow";
import type {
  TestimonialRequestFlowSettings,
  TestimonialRequestFlowPatch,
} from "@shared/testimonialRequestFlow";
import type {
  PastClientReactivationFlowSettings,
  PastClientReactivationFlowPatch,
} from "@shared/pastClientReactivationFlow";
import type { FooterModel } from "@shared/footerModel";
import { notifyTaskChanged } from "./taskChangeSignal";
import { expectedWorkspaceHeaders } from "./tenantIsolation";
import { isWorkspaceWrite } from "@shared/workspaceContract";
import { contactFieldsApi } from "./contactFieldsApi";
import type { TrackableDestinationOption } from "@shared/websiteButtonDestinations";

export type { AiVideoJob, AiVideoJobPayload } from "@shared/schema";

export type SessionTypeSlot = {
  id: number;
  dayOfWeek: number;
  specificDate?: string | null;
  startTime: string; // "HH:MM"
  durationMinutes: number;
  capacity: number;
  rsvpEnabled: boolean;
  nextDate?: string; // "YYYY-MM-DD"
};

export type SessionAvailabilityWindow = {
  id: number;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  durationMinutes: number;
  incrementMinutes: number;
};

export type SessionType = {
  id: number;
  title: string;
  description: string;
  // "group" = a class/group session; "one-on-one" = individual training slot.
  classification: "group" | "one-on-one";
  sessionPurpose: "regular" | "consultation";
  mediaUrl: string;
  mediaFit?: "fit" | "fill";
  mediaFocalX?: number;
  mediaFocalY?: number;
  mediaZoom?: number;
  slots: SessionTypeSlot[];
  availabilityWindows: SessionAvailabilityWindow[];
  serviceLeadSettings?: import("@shared/appointmentServiceSettings").ServiceLeadSettings;
  bookingNotificationSettings: {
    emailEnabled: boolean;
    textEnabled: boolean;
    emailSubject: string;
    emailBody: string;
    emailOptions: CheckinNotificationEmailOptions;
    textBody: string;
    actions?: {
      relationshipTags: string[];
      createTask: boolean;
      taskTitle: string;
      daysOffset: number;
      dateAnchor: "booking" | "appointment";
      folder: string;
    };
  };
};

export type SessionTypeInput = {
  leadSettings?: import("@shared/appointmentServiceSettings").AppointmentLeadSettings;
  leadSettingsCategory?: import("@shared/appointmentServiceSettings").AppointmentCategory;
  title: string;
  description?: string;
  classification?: "group" | "one-on-one";
  sessionPurpose?: "regular" | "consultation";
  mediaUrl?: string;
  mediaFit?: "fit" | "fill";
  mediaFocalX?: number;
  mediaFocalY?: number;
  mediaZoom?: number;
  bookingNotificationSettings?: {
    emailEnabled: boolean;
    textEnabled: boolean;
    emailSubject: string;
    emailBody: string;
    emailOptions: CheckinNotificationEmailOptions;
    textBody: string;
    actions?: {
      relationshipTags: string[];
      createTask: boolean;
      taskTitle: string;
      daysOffset: number;
      dateAnchor: "booking" | "appointment";
      folder: string;
    };
  };
  // Existing slot IDs to keep + update (present = update; absent = delete).
  slots?: Array<{
    id?: number; // absent = new slot
    dayOfWeek: number;
    startTime: string;
    durationMinutes: number;
    capacity: number;
    rsvpEnabled: boolean;
  }>;
  availabilityWindows?: Array<{
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    durationMinutes: number;
    incrementMinutes: number;
  }>;
};

export type OneOnOneBookingInput = {
  requestId?: string;
  date: string;
  startTime: string;
  durationMinutes: number;
  leadId: number;
  // Entry-only fields; omitted values retain the reusable label's legacy text.
  appointmentTitle?: string;
  appointmentNotes?: string;
  sessionPurpose?: "regular" | "consultation";
};

export type NewOneOnOneBookingInput = OneOnOneBookingInput & {
  leadSettings?: import("@shared/appointmentServiceSettings").AppointmentLeadSettings;
  // title is the legacy shared label. New clients should send label and
  // appointmentTitle separately.
  title?: string;
  label?: string;
  description?: string; // legacy reusable-label description
  sessionPurpose?: "regular" | "consultation";
};

export type PrivateOneOnOneAppointment = {
  id: number;
  title: string;
  date: string;
  startTime: string;
  durationMinutes: number;
  notes: string;
  sessionTypeId: number | null;
  label: string;
  leadId: number | null;
  contactName: string;
};

export type PrivateOneOnOneAppointmentUpdateInput = {
  title: string;
  notes: string;
  date: string;
  startTime: string;
  durationMinutes: number;
  // Omit this to retain the existing label, including legacy unassigned
  // appointments whose sessionTypeId is null.
  sessionTypeId?: number;
  // The edit form can retain an unassigned legacy appointment while it asks
  // the owner to select a CRM contact; the server rejects null on submission.
  leadId: number | null;
};

// ── Reactivate Past Customers (Task 1819) ──
// Resolved master summary for the goal picker (defaults + admin overrides).
export type LegalKind = "terms" | "privacy";
export type LegalDocResponse = { kind: LegalKind; sections: LegalSection[]; defaults: LegalSection[]; isCustom: boolean };
export type PlatformLegalDocResponse = LegalDocResponse & {
  scope: "platform";
  format: "rich-text";
  updated: string;
};

// SEO tab: the saved overrides plus the derived defaults used to pre-fill inputs.
export type SeoDefaults = Required<{ [K in keyof SeoMeta]: string }>;
export type SeoResponse = { seo: SeoMeta; defaults: SeoDefaults };

export type AiVideoCreateInput = {
  script: string;
  pendingMediaName?: string;
  // Render with the account's active on-screen avatar (the server resolves the
  // avatar + voice ids). Used when no explicit avatar-library row is chosen.
  // (The legacy presenterVideoId field — a presenter library entry id — was
  // retired July 2026; the avatar library below is the only explicit picker.)
  presenterFromProfile?: boolean;
  // Render from a SPECIFIC avatar-library row (custom_avatars — user-built or
  // seeded starter). The avatar's configured voice is always used; there is no
  // separate voice choice. Used by the campaign Offer/After video slots.
  customAvatarId?: number;
  purpose?: string | null;
  label?: string | null;
  // Link the job to a Website content item so the inline editor can show the
  // generating/finished state in place.
  attachedEntity?: string | null;
  attachedId?: number | null;
  // Replace intent: the media URL currently in the attached item's slot when
  // the render starts. The server may swap the finished clip in only while
  // the slot still holds this exact URL.
  replacesMediaUrl?: string;
  // Avatar IV performance controls (July 2026). Optional — the server
  // defaults expressiveness to "medium". The Acting Directions box was
  // removed July 2026: every render uses the server's default motion
  // baseline (composeMotionPrompt in server/heygen.ts). Engine picker also
  // removed — every render uses the Standard (avatar_iv) engine; the server
  // keeps avatar_v support only for regenerating old jobs.
  expressiveness?: "low" | "medium" | "high";
};

export type AiVideoQuota = {
  used: number;
  max: number | null;
  maxSeconds: number;
  unlimited?: boolean;
  // Monthly SPEND allowances (July 2026): generations this month — deleting a
  // video never refunds a unit. resetsOn = first of next month (ISO date).
  monthly?: {
    videoUsed: number;
    videoMax: number;
    avatarUsed: number;
    avatarMax: number;
    resetsOn: string;
  };
};

// Saved videos are unlimited. Only the monthly generation allowance can block
// generation (deleting doesn't help — resets next month). Exempt
// (unlimited) accounts are never blocked.
export function videoGenCapReason(q: AiVideoQuota | undefined): "monthly" | null {
  if (!q || q.unlimited) return null;
  if (q.monthly && q.monthly.videoUsed >= q.monthly.videoMax) return "monthly";
  return null;
}

// Text Messaging (BYO Twilio). The auth token is write-only — only a boolean
// "hasAuthToken" flag ever comes back.
export type SettingsEmailConfig = {
  fromName: string;
  // True when composed email can be sent (a usable Resend key + from address
  // resolve — either the user's own, or the platform key for the owner).
  configured: boolean;
  fromAddress: string;
  // True when this user has their own Resend API key saved (write-only — the
  // key itself is never returned).
  hasApiKey: boolean;
  // The raw saved "From" email (may be blank); seeds the onboarding/settings
  // From-Email field without falling back to a computed default.
  resendFromEmail: string;
  managedEmailDomain?: string | null;
};

export type ManagedEmailDomainConfig = {
  domain: string | null;
  domainId: string | null;
  status: string;
  records: unknown[];
  replyTo: string;
  migrationState: string;
  suspended?: boolean;
  dmarc?: {
    status: "direct" | "inherited" | "missing" | "invalid" | "unavailable";
    domain: string;
    organizationalDomain: string;
    checkedDomain: string | null;
    record: string | null;
    suggestedRecord: string | null;
    reason?: string;
  } | null;
};

export type ManagedEmailOperationalSummary = {
  trackingStartDate?: string | null;
  workspaceId: string;
  domain: string | null;
  domainId: string | null;
  lifecycle: string;
  lifecycleTransitions?: EmailLifecycleTransition[];
  unresolvedDomainCleanup?: Array<{
    id: string;
    operationId: string;
    kind: string;
    domain: string;
    resourceId: string | null;
    state: string;
    attempts: number;
    detail: string | null;
    nextAttemptAt: string;
  }>;
  migrationState: string;
  suspension: {
    suspended: boolean;
    reason: string | null;
    at: string | null;
    actor: string | null;
  };
  suspensionHistory: Array<{
    action: "suspended" | "resumed";
    reason: string | null;
    at: string;
    actor: string;
  }>;
  counts: {
    sent: number;
    delivered: number;
    bounced: number;
    complained: number;
    suppressed: number;
    unconfirmed?: number;
  };
  rates?: {
    delivery: number | null;
    bounce: number;
    complaint: number;
    suppression: number;
  };
  firstManagedSendAt?: string | null;
  domainAgeDays?: number | null;
};

export type EmailTransportStatus = {
  platform: {
    purpose: string;
    active: boolean;
    configured: boolean;
    currentFrom: string;
    targetFrom: string;
    replyTo: string;
  };
  owner: {
    purpose: string;
    active: boolean;
    configured: boolean;
    currentFrom: string;
    replyTo: string;
  };
  customerManaged: {
    purpose: string;
    configured: boolean;
    domainPattern: string;
  };
};

export type EmailSettingsUpdate = {
  fromName?: string;
  replyTo?: string;
  resendFromEmail?: string;
  resendApiKey?: string;
};

export type BulkEmailDirectResponse = {
  queued?: false;
  sent: number;
  failed: number;
  total: number;
  errors: string[];
  failedContactIds: number[];
  skippedDoNotEmail?: number;
};

export type BulkEmailQueuedResponse = {
  queued: true;
  batchId: number;
  recipientCount: number;
  skipped: number;
};

export type BulkEmailResponse = BulkEmailDirectResponse | BulkEmailQueuedResponse;

export type EmailMetricCounts = {
  received: number;
  sent: number;
  delivered: number;
  delivery_delayed: number;
  failed: number;
  suppressed: number;
  bounced: number;
  opened: number;
  unique_opened: number;
  clicked: number;
  unique_clicked: number;
  complained: number;
  unsubscribed: number;
};

export type EmailMetricsResponse = {
  trackingStartDate?: string | null;
  configured: boolean;
  source?: "local";
  retentionLimited?: boolean;
  // Safe failure category only; provider response details never cross the API.
  // The response still contains locally recorded unsubscribe history.
  providerFailure?: "temporary" | "action_required" | "internal_error";
  hasLocalUnsubscribeHistory: boolean;
  startDate: string;
  endDate: string;
  // True when one or more provider batches succeeded before a later batch failed.
  partial?: boolean;
  // Safe aggregate coverage for partial provider reports. Provider message IDs
  // and workspace identifiers never cross this API boundary.
  coverage?: {
    coveredSends: number;
    totalTrackedSends: number;
  };
  totals: EmailMetricCounts;
  daily: Array<EmailMetricCounts & { date: string }>;
  rates: {
    delivery: number;
    open: number;
    click: number;
    bounce: number;
    complaint: number;
    unsubscribe: number;
  };
  hasTrackedSends: boolean;
};

// ── Newsletter Sync (Settings → Email → Newsletter Sync) ────────────────
// The beehiiv API key is write-only: the server returns only hasApiKey.
export type NewsletterSyncConfig = {
  beehiiv: {
    hasApiKey: boolean;
    publicationId: string;
    enabled: boolean;
    lastSyncAt: string | null;
    lastSyncError: string | null;
  };
  webhook: {
    url: string;
    enabled: boolean;
    lastSyncAt: string | null;
    lastSyncError: string | null;
  };
};

export type NewsletterSyncUpdate = {
  beehiivApiKey?: string;
  beehiivPublicationId?: string;
  beehiivEnabled?: boolean;
  webhookUrl?: string;
  webhookEnabled?: boolean;
};

export type NewsletterPublication = { id: string; name: string };

export type BaselineResponse = {
  leadsPerMonth: number | null;
  clientValueUsd: number | null;
  salesPerMonth: number | null;
  websiteUrl: string;
};

export type BaselineUpdate = {
  leadsPerMonth?: number | null;
  clientValueUsd?: number | null;
  salesPerMonth?: number | null;
  websiteUrl?: string;
};

// A single DNS record the owner adds to point / verify their custom domain.
export type CustomDomainDnsRecord = { type: string; name: string; value: string };

// Custom-domain state for the public Vault page (Task #668). `configured` is
// false when the Cloudflare integration isn't set up (dev/before-setup) — the UI
// shows a "coming soon" note and disables the form in that case.
export type CustomDomainResponse = {
  configured: boolean;
  domain: string;
  status: string; // "" | "pending" | "active" | "error"
  // Plain-English status detail for the owner. Set for BOTH "pending" (which
  // waiting state we're in — DNS not pointed yet vs. finishing HTTPS) and
  // "error" (what's actually wrong). The UI keys its tone off `status`.
  error: string;
  // True when this domain has sat broken (pending/error) longer than the
  // auto-recovery window, so the background monitor has stopped re-polling it
  // (Task #705/#713). The Domain tab shows a gentle note telling the owner to
  // click "Check Status" by hand once their DNS is fixed.
  recoveryPaused?: boolean;
  dnsRecord: CustomDomainDnsRecord | null;
  // Root/apex domains are also served on their www twin (www.yourbusiness.com)
  // — this is the second A record the owner adds. Null for sub-domains.
  wwwDnsRecord: CustomDomainDnsRecord | null;
  verification: CustomDomainDnsRecord[];
};

export type BusinessProfileResponse = {
  /** Preserved conflicting legacy value; only Testimonial Request can resolve it. */
  previousGoogleReviewUrl?: string;
  businessName: string;
  // Server-managed canonical Free Consultation flow placement. Owners edit
  // only this visibility flag from its dedicated Flow page.
  freeConsultationWebsiteTabId: number | null;
  freeConsultationBusinessSlideId: number | null;
  freeConsultationShowOnWebsite: boolean;
  // Server-managed canonical Get Pricing slide in the built-in Get Started
  // section. The tab id is legacy-only and normally null after repair.
  getPricingWebsiteTabId: number | null;
  getPricingBusinessSlideId: number | null;
  getPricingShowOnWebsite: boolean;
  // Server-computed fallback for the email-signature website when the baseline
  // websiteUrl is blank: the live custom domain (absolute https URL) or the
  // public /p/<slug> page (relative — client resolves against its origin).
  signatureFallbackWebsiteUrl: string;
  tagline: string;
  // Footer ghost-watermark word (blank = fall back to businessName).
  footerWord: string;
  // Footer watermark mode: "logo" (faded logo, default) or "word" (the ghost
  // word above / business name) — for owners with no logo or who prefer text.
  footerBrandMode: "none" | "logo" | "word";
  // Footer-only logo override. Empty = use the onboarding wide logo / logo.
  footerLogoUrl: string;
  // Footer watermark intensity; 50 preserves the original appearance.
  footerBrandOpacity: number;
  footerTaglineOpacity: number;
  // Website → Effects controls (opacity/intensity settings shared with the
  // public site renderer). Glow/burst ranges are 0–200 (middle glows default
  // 100, footer burst 0); Middle Word opacity is 0–100 (default 8) and the UI
  // presents its inverse as transparency.
  footerAccentBurstVisibility: number;
  middleBackgroundGlow: number;
  middleBackgroundColor: string;
  middleBackgroundUrl: string;
  middleHideSingleTab: boolean;
  pagesBackgroundColor: string;
  pagesBackgroundUrl: string;
  pagesBackgroundGlow: number;
  pagesBackgroundOpacity: number;
  websitePagesMenuPlacement?: "none" | "header" | "footer" | "both";
  websitePagesMenuFontColor?: string;
  websitePagesMenuOrder?: string;
  blogBackgroundColor: string;
  blogBackgroundUrl: string;
  blogBackgroundGlow: number;
  blogBackgroundOpacity: number;
  middleCardGlow: number;
  middleWordOpacity: number;
  // Whether the tagline also renders under the footer watermark brand.
  footerShowTagline: boolean;
  // Footer booking button (shown when bookingUrl is set): toggle + label
  // (blank = "Book Now" default).
  footerShowBookingButton: boolean;
  footerBookingLabel: string;
  footerCtaUrl: string;
  // A short, reusable plain-text description of what the business does.
  // Captured in onboarding (AI-tidied) and editable on Website → Branding; fed
  // into the campaign-draft prompt as context. Not shown on the public page.
  businessDescription: string;
  // Owner's personal name and email — collected at the top of the Basics
  // onboarding step and fed into the mirrored CRM signup lead. CRM feed only;
  // does not change the Clerk login email.
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string;
  logoUrl: string;
  accentColor: string;
  notificationEmails: string[];
  notifyOnLead: boolean;
  // Owner lead-alert SMS ("text me when a new lead comes in") + the mobile
  // number those alerts go to. Managed in Settings → Lead Capture.
  notifyLeadBySms: boolean;
  notificationMobile: string;
  // Per-surface SMS lead alerts (share notificationMobile above): the Digital
  // Receptionist / website-contact form and the testimonial form.
  contactNotifyLeadBySms: boolean;
  testimonialNotifyLeadBySms: boolean;
  // "Also text me important notifications" (Task 2076): system notifications
  // (domain down, broken automatic email/texting, receptionist nudges) also
  // text notificationMobile. Managed in Settings → Notifications.
  notifySystemBySms: boolean;
  // Google-style hours of operation (Task #1331) — collected in onboarding
  // Basics, shown on the public footer. See shared/businessHours.ts.
  businessHours: BusinessHours;
  localBusinessType: import("zod").z.infer<typeof import("@shared/localBusinessSchema").localBusinessTypeSchema>;
  businessOperatingModel: import("zod").z.infer<typeof import("@shared/localBusinessSchema").businessOperatingModelSchema>;
  serviceAreas: string[];
  schemaHoursReviewed: boolean;
  requireFirstName: boolean;
  requireLastName: boolean;
  requirePhone: boolean;
  requireAddressLine1: boolean;
  requireAddressLine2: boolean;
  requireCity: boolean;
  requireRegion: boolean;
  requirePostalCode: boolean;
  phone: string;
  // Footer phone-link behavior: "call" → tel: (click-to-call, default), "text"
  // → sms: (opens a new text), "both" → Call | Text links, "none" → no link.
  // Only affects the public footer link.
  footerPhoneAction: "call" | "text" | "both" | "none";
  // Optional starter message pre-filled into the visitor's texting app when the
  // footer action is "text". Blank → a plain sms: link. Ignored for "call".
  footerPhoneText: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  addressNotApplicable: boolean;
  socialLinks: string[];
  // Public Google Maps / Google Business Profile listing. Kept separately from
  // the direct review URL so settings can show both intents clearly.
  googleBusinessProfileUrl: string;
  wideLogoUrl: string;
  // Display height (px) of the primary logo in the public website header bar
  // (fixed 80px tall). Slider-adjusted in the Logo uploader; default 40.
  headerLogoHeight: number;
  // Derived (or owner-chosen) SQUARE icon for the dashboard sidebar, the browser
  // favicon, and the PWA manifest icon. Produced by the single "Upload Your Logo"
  // flow. Empty = fall back to `logoUrl` everywhere it's consumed.
  logoIconUrl: string;
  // Pre-edit logo pointers behind the "Remove Background" / "Lighten" helper
  // edits — persisted so "Restore Original" survives a reload. Cleared by a
  // fresh manual upload or a restore. Empty = nothing to restore.
  previousLogoUrl: string;
  previousWideLogoUrl: string;
  accentColorLight: string;
  accentColorDark: string;
  // ONE shared hero + footer background color for the public website.
  // "" = unset → the template's default black design (Task #1652).
  heroFooterBgColor: string;
  lightColor: string;
  videoImageOriginalUrl: string;
  // Shared scripted-hero lead-in clips (Task #524): a Welcome greeting (also the
  // resting poster), an Ask clip at the first prompt, and a Waiting b-roll that
  // loops across the remaining prompts + while the lead sends. Account-level so
  // every pricing campaign reuses them. Empty = fall back to the campaign clip.
  heroWelcomeVideoUrl: string;
  heroAskVideoUrl: string;
  heroWaitingVideoUrl: string;
  // Studio-composite provenance for the three shared hero clip slots. These
  // slots hold a video by default, but a photo can be composited onto the
  // fixed standard dark studio plate via MediaFormatPicker; when it is, the
  // as-uploaded original (hero*OriginalUrl) is persisted so the photo can be
  // re-edited/reverted. Empty = the slot holds a video or a plain photo.
  heroWelcomeOriginalUrl: string;
  heroAskOriginalUrl: string;
  heroWaitingOriginalUrl: string;
  // Spoken welcome-video script the presenter narrates (drafted at onboarding
  // from the business name/description, editable on the Avatar step). Empty =
  // the onboarding renderer re-drafts one at Finish.
  heroWelcomeScript: string;
  // Fingerprint (businessName + "::" + businessDescription) of the Basics that
  // were used when the hero draft was last produced. Blank = never drafted or
  // pre-dates this field. Compared by the Avatar step to show the "details
  // changed" emphasis on the Regenerate button.
  heroDraftBasicsSnapshot: string;
  heroBullets: string;
  statChipValue: string;
  statChipLabel: string;
  // Fixed word for the tabbed section's vertical ghost watermark (blank =
  // fall back to footerWord, then businessName).
  middleGhostWord: string;
  // Movable/renamable built-in website sections (Library grid). NavPos = index
  // into the interleaved nav-order key list (null = default, after the user
  // tabs); labels empty = default ("Get Started" / "Client Feedback" / "Sessions").
  websiteOffersNavPos: number | null;
  websiteFeedbackNavPos: number | null;
  websiteSessionsNavPos: number | null;
  websiteBlogNavPos: number | null;
  websiteOffersShowOnWebsite: boolean;
  websiteFeedbackShowOnWebsite: boolean;
  // Public Website/Vault visibility for the built-in Sessions section.
  // Missing legacy rows/payloads are treated as visible.
  websiteSessionsShowOnWebsite: boolean;
  websiteOffersLabel: string;
  websiteFeedbackLabel: string;
  websiteSessionsLabel: string;
  websiteBlogLabel: string;
  websiteFloatingButtonVisibility: import("@shared/floatingButtonVisibility").FloatingButtonVisibility;
  websiteBlogListing: import("@shared/blogListing").BlogListingSettings;
  websiteBlogPlacement: import("@shared/blogPlacement").BlogPlacement;
  // Per-tab ghost-word + stat-badge overrides for the BUILT-IN sections
  // (Get Started / Client Feedback / Sessions). Blank = the site-wide values.
  websiteOffersGhostWord: string;
  websiteOffersStatValue: string;
  websiteOffersStatLabel: string;
  websiteFeedbackGhostWord: string;
  websiteFeedbackStatValue: string;
  websiteFeedbackStatLabel: string;
  websiteSessionsGhostWord: string;
  websiteSessionsStatValue: string;
  websiteSessionsStatLabel: string;
  // Uploaded-video presenter defaults (reusable HeyGen Digital Twin + cloned
  // voice, built once from footage). Server-managed — never sent on PATCH.
  videoPresenterAvatarId: string;
  videoPresenterVoiceId: string;
  videoPresenterVideoUrl: string;
  // Ready-made AI avatar (Task #654). videoAvatarMode selects the account's
  // active on-screen avatar: "twin" uses the videoPresenter* digital twin;
  // "stock" uses videoStockAvatarId + videoStockVoiceId (a curated HeyGen stock
  // avatar + paired stock voice — no clone, no consent). Server-managed.
  videoAvatarMode: "twin" | "stock" | "custom";
  videoStockAvatarId: string;
  videoStockVoiceId: string;
  // "You or custom" avatar (Task #656). videoAvatarMode="custom" renders a
  // photo/prompt HeyGen avatar through the Avatar IV engine. kind is "photo" (one
  // uploaded still of the owner) or "ai" (a text-described face); the build is
  // async so status walks ""→"creating"→"training"→"ready"|"failed" and the
  // client polls this profile. previewUrl is the face shown for approval,
  // sourceImageUrl the owner's uploaded photo (or the generated preview for
  // "ai"). voiceKind is "builtin" (HeyGen TTS, default) or "clone" (ElevenLabs,
  // photo path only). Server-managed via /api/presenter/custom.
  videoCustomAvatarKind: "" | "photo" | "ai";
  videoCustomAvatarId: string;
  videoCustomSourceImageUrl: string;
  videoCustomPreviewUrl: string;
  videoCustomVoiceKind: "builtin" | "clone";
  // Per-build background baked into the custom avatar (Task #696): "original"
  // keeps the photo's / AI background; "professional" is the charcoal studio look;
  // "custom" composites onto videoCustomBackgroundUrl (photo path only).
  videoCustomBackground: "original" | "professional" | "custom";
  // The owner-uploaded background still when videoCustomBackground is "custom".
  videoCustomBackgroundUrl: string;
  // Which professional plate was baked in when videoCustomBackground is
  // "professional" (Task #1417): the default charcoal studio look, a light white
  // plate, or a plate tinted with the brand accent color.
  videoCustomProfessionalVariant: "charcoal" | "white" | "accent";
  // The per-user accent professional plate's CDN url — a code-drawn gradient
  // tinted with the brand accent, generated once at onboarding finish and
  // regenerated on demand when the accent changes. "" until generated.
  accentBackgroundUrl: string;
  videoCustomStatus: "" | "creating" | "training" | "ready" | "failed";
  videoCustomError: string;
  // Owner-chosen Website tab name overrides. Empty string = use the built-in
  // default label for that section ("Our Services", "Take a Tour", etc.).
  tabLabelServices: string;
  tabLabelGallery: string;
  tabLabelTeam: string;
  tabLabelTestimonials: string;
  tabLabelFaqs: string;
  // Independent field picker for the website Contact form's Send a Message form.
  // Separate from the lead-capture require* flags — affects ONLY this form.
  // Email + Message are always collected (not represented here).
  contactFormFirstName: boolean;
  contactFormLastName: boolean;
  contactFormPhone: boolean;
  contactFormFirstNameRequired: boolean;
  contactFormLastNameRequired: boolean;
  contactFormPhoneRequired: boolean;
  contactFormAddressLine1: boolean;
  contactFormAddressLine2: boolean;
  contactFormCity: boolean;
  contactFormRegion: boolean;
  contactFormPostalCode: boolean;
  // Per-address-field REQUIRED flags for the website Contact form. Enforced
  // only when the matching contactForm* show flag above is also on.
  contactFormAddressLine1Required: boolean;
  contactFormAddressLine2Required: boolean;
  contactFormCityRequired: boolean;
  contactFormRegionRequired: boolean;
  contactFormPostalCodeRequired: boolean;
  // Standalone Contact copy (account-level). Drives the public footer "Contact
  // Us" modal (headline / intro / submit-button label). Blank = client default.
  contactHeadline: string;
  contactIntro: string;
  contactCtaLabel: string;
  // Digital Receptionist (Task #1491). Owner-chosen display name for the
  // public-page KB-grounded chat widget ("" = generic introduction), plus the
  // owner's most recent "my Knowledgebase is accurate" attestation timestamp
  // (ISO string; null = never confirmed). The confirmation is stamped via its
  // own endpoint, never through the profile PATCH.
  receptionistName: string;
  receptionistEnabled: boolean;
  receptionistLauncherText: string;
  receptionistLauncherImageUrl: string;
  receptionistLauncherImageZoom: number;
  receptionistLauncherImageFocalX: number;
  receptionistLauncherImageFocalY: number;
  receptionistLauncherSubtext: string;
  receptionistKbConfirmedAt: string | null;
  // Server-stamped when the one-time "pending questions" reminder email went
  // out (see server/receptionistPendingNudge.ts) — read-only, never PATCHed.
  receptionistPendingRemindedAt: string | null;
  // Receptionist conversation transcripts (Task 1704): keep anonymous chats
  // server-side and email them to the owner (per-chat or a daily digest).
  // Email blank = default (notificationEmails[0], else the account email).
  receptionistTranscriptsEnabled: boolean;
  receptionistTranscriptMode: "per_chat" | "daily";
  receptionistTranscriptEmail: string;
  // Receptionist lead capture and owner notification settings are independent
  // from the website Contact form.
  receptionistCollectFirstName: boolean;
  receptionistCollectLastName: boolean;
  receptionistCollectPhone: boolean;
  receptionistFirstNameRequired: boolean;
  receptionistLastNameRequired: boolean;
  receptionistPhoneRequired: boolean;
  receptionistCollectAddressLine1: boolean;
  receptionistCollectAddressLine2: boolean;
  receptionistCollectCity: boolean;
  receptionistCollectRegion: boolean;
  receptionistCollectPostalCode: boolean;
  receptionistAddressLine1Required: boolean;
  receptionistAddressLine2Required: boolean;
  receptionistCityRequired: boolean;
  receptionistRegionRequired: boolean;
  receptionistPostalCodeRequired: boolean;
  receptionistNotifyOnLead: boolean;
  receptionistNotifyLeadByEmail: boolean;
  receptionistNotificationEmails: string[];
  receptionistNotificationMobiles: string[];
  receptionistNotifyLeadBySms: boolean;
  receptionistNotificationMobile: string;
  receptionistNotificationEmailSubject: string;
  receptionistNotificationEmailBody: string;
  receptionistNotificationEmailOptions: CheckinNotificationEmailOptions;
  receptionistNotificationTextBody: string;
  receptionistConfirmationMessage: string;
  receptionistConfirmationMode?: "message" | "page";
  receptionistConfirmationUrl?: string;
  receptionistAutoCreateFollowUpTask: boolean;
  receptionistFollowUpTaskTitle: string;
  receptionistFollowUpTaskDaysOut: number;
  receptionistFollowUpTaskFolder: string;
  receptionistKeepLeadTag: boolean;
  receptionistRelationshipTags: string[];
  receptionistAddToPipeline: boolean;
  receptionistDefaultPipelineStageSlug: string;
  // Post-submit "thank you" copy for the Contact modal. "" = fall back to
  // DEFAULT_FORM_CONFIRMATION at render time (blank-as-default).
  contactConfirmationMessage: string;
  contactConfirmationMode?: "message" | "page";
  contactConfirmationUrl?: string;
  // Follow-up email sent to the submitter after a Contact-form message (BYO
  // inbox). Disabled by default; subject/body blank = no send even if enabled.
  contactFollowUpEmailEnabled: boolean;
  contactFollowUpEmailSubject: string;
  contactFollowUpEmailBody: string;
  contactFollowUpEmailHeaderImageUrl: string;
  // Contact tab LEAD HANDLING (account-level, campaign-less). Mirrors the per-
  // campaign lead-handling controls for the website Contact form. Defaults
  // preserve today's behavior (no follow-up task, keep the "Lead" tag, add to
  // pipeline). Applied server-side in /api/ic/submit for website-contact
  // submissions; Source/Direction/Channel stay locked.
  contactAutoCreateFollowUpTask: boolean;
  contactFollowUpTaskTitle: string;
  contactFollowUpTaskDaysOut: number;
  contactFollowUpTaskFolder: string;
  contactKeepLeadTag: boolean;
  contactRelationshipTags: string[];
  contactAddToPipeline: boolean;
  // Slug into the user's pipeline_stages; "" = first stage.
  contactDefaultPipelineStageSlug: string;
  // Testimonial collection (Task #650): when true, the public Client Feedback
  // carousel shows a "submit your own testimonial" form on its final slide (only
  // when published testimonials already exist). testimonialNotifyEmails receives
  // the new-submission alert (falls back to the campaign lead-notify address).
  enableTestimonialSubmission: boolean;
  testimonialNotifyEmails: string[];
  // Testimonial LEAD HANDLING — mirrors the contact* block, driven by the shared
  // LeadCaptureSettings block on Website → Testimonials (with "What Info to
  // Collect" hidden). Applied server-side in /api/ic/testimonial. Testimonial-
  // specific defaults: notify on, follow-up task on (replaces the old always-
  // created review task), tag "IC - Testimonial Received" instead of "Lead".
  testimonialNotifyOnLead: boolean;
  testimonialFollowUpEmailEnabled: boolean;
  testimonialFollowUpEmailSubject: string;
  testimonialFollowUpEmailBody: string;
  testimonialFollowUpEmailHeaderImageUrl: string;
  testimonialAutoCreateFollowUpTask: boolean;
  testimonialFollowUpTaskTitle: string;
  testimonialFollowUpTaskDaysOut: number;
  testimonialFollowUpTaskFolder: string;
  testimonialKeepLeadTag: boolean;
  testimonialRelationshipTags: string[];
  // Post-submit copy for the testimonial form's done state. "" = default.
  testimonialConfirmationMessage: string;
  testimonialConfirmationMode?: "message" | "page";
  testimonialConfirmationUrl?: string;
  // Master lead-capture DEFAULTS (account-level), edited on Settings → Lead
  // Capture → Default via the shared LeadCaptureSettings block. These seed the
  // lead-handling columns of every NEW campaign at creation and pre-fill the
  // Campaign Creator form; existing campaigns are never changed.
  // Mirrors the unified LeadCaptureSettingsValue shape ("" title = default).
  leadDefaultCollectFirstName: boolean;
  leadDefaultCollectLastName: boolean;
  leadDefaultCollectPhone: boolean;
  leadDefaultFirstNameRequired: boolean;
  leadDefaultLastNameRequired: boolean;
  leadDefaultPhoneRequired: boolean;
  leadDefaultCollectAddressLine1: boolean;
  leadDefaultCollectAddressLine2: boolean;
  leadDefaultCollectCity: boolean;
  leadDefaultCollectRegion: boolean;
  leadDefaultCollectPostalCode: boolean;
  // Per-address-field REQUIRED defaults — seed new campaigns + pre-fill wizard.
  leadDefaultAddressLine1Required: boolean;
  leadDefaultAddressLine2Required: boolean;
  leadDefaultCityRequired: boolean;
  leadDefaultRegionRequired: boolean;
  leadDefaultPostalCodeRequired: boolean;
  leadDefaultNotifyOnLead: boolean;
  leadDefaultNotificationEmails: string[];
  leadDefaultFollowUpEmailEnabled: boolean;
  leadDefaultFollowUpEmailSubject: string;
  leadDefaultFollowUpEmailBody: string;
  leadDefaultFollowUpEmailHeaderImageUrl: string;
  leadDefaultAutoCreateFollowUpTask: boolean;
  leadDefaultFollowUpTaskTitle: string;
  leadDefaultFollowUpTaskDaysOut: number;
  leadDefaultAddToPipeline: boolean;
  leadDefaultPipelineStageSlug: string;
  leadDefaultKeepLeadTag: boolean;
  leadDefaultRelationshipTags: string[];
  // Default post-submit copy seeded into NEW campaigns' confirmationMessage.
  leadDefaultConfirmationMessage: string;
  leadDefaultConfirmationMode?: "message" | "page";
  leadDefaultConfirmationUrl?: string;
  // Online booking link (Task 1742) — the owner's scheduler URL. Blank = the
  // public Book section and Digi's booking answer are both disabled.
  bookingUrl: string;
  // Booking pill/CTA target (Task 2143): "external" scheduler link (default)
  // or "schedule" (in-page class schedule / Save Your Spot).
  bookingTarget: "external" | "schedule";
  // Google review link (Task 1996) — the owner's "leave us a Google review"
  // URL. Blank = the {{googleReviewLink}} merge-tag line drops from sends.
  googleReviewUrl: string;
  // Booking → CRM webhook token (Task 1744). Blank = not connected. Managed
  // through api.bookingWebhook (never through the profile PATCH).
  bookingWebhookToken: string;
  // Optional Calendly webhook signing key (Task 1747). Blank = token-only
  // auth; when set, this connection accepts only validly signed Calendly calls.
  calendlySigningKey: string;
};

// A saved, re-usable custom avatar (Task #656 follow-up). Picking one skips the
// one-time HeyGen avatar-creation cost — only the per-render Avatar IV fee applies.
export type CustomAvatar = {
  id: number;
  userId: string;
  kind: "photo" | "ai";
  name: string;
  heygenAvatarId: string;
  heygenGroupId: string;
  previewUrl: string;
  sourceImageUrl: string;
  voiceId: string;
  // "heygen" = HeyGen-native cloned voice (built-in starters, Task #1463);
  // renders like "builtin" but distinguishable. "clone" = ElevenLabs clone.
  voiceKind: "builtin" | "clone" | "heygen";
  background?: "original" | "professional" | "custom";
  backgroundUrl?: string;
  sampleVideoUrl?: string;
  seeded?: boolean;
  // Extra-look lifecycle (Task #1180). Pre-existing avatars are "ready".
  //   training → HeyGen is still building the look (poll the library)
  //   pending  → trained; awaiting the owner's preview-and-approve decision
  //   failed   → the build errored (buildError says why); discard only
  //   ready    → approved and selectable
  status?: "training" | "pending" | "failed" | "ready";
  buildError?: string;
  // Per-look label within a group (Task #1196). Blank = default — the looks
  // modal shows "Look 1/2/…" by position.
  lookLabel?: string;
  // Which ready look fronts the group's card tile in the library (Task #1196).
  // At most one per group; false everywhere = the base look fronts the card.
  cardLook?: boolean;
  // Owner-chosen position within the look's group (Task #1222). 0 = never
  // explicitly ordered — sorts first, then by creation order.
  sortOrder?: number;
  // Owner-chosen position of this row's PERSON (group) in the library grid
  // (Task #1231). Shared across a group's rows (effective order = the group's
  // max). 0 = never explicitly ordered — those people sort first, newest-first.
  personSortOrder?: number;
  // Only set for seeded (built-in) avatars — drives the Built-In Men/Women
  // library folders. Undefined for user-built avatars (they live in "Custom").
  gender?: "male" | "female";
  // When the likeness attestation for this avatar's person (HeyGen group) was
  // recorded (Task 1995) — ISO timestamp of the earliest consent record.
  // Undefined = built before attestations were persisted ("not recorded"), or
  // a seeded starter (platform likeness, no attestation needed).
  attestedAt?: string;
  createdAt: string;
};

// One Q&A pair the public-page Digital Receptionist may answer from. The chat
// model only sees APPROVED rows; pending rows (onboarding-seeded or
// slide-generated) wait for owner sign-off on Marketing → Digital Receptionist.
export type ReceptionistKbEntry = {
  id: number;
  userId: string;
  question: string;
  answer: string;
  status: "pending" | "approved";
  source: "onboarding" | "manual" | "slide" | "playbook";
  sourceSlideId: number | null;
  sourceCampaignId: number | null;
  draftedQuestion: string | null;
  draftedAnswer: string | null;
  approvedAt: string | null;
  approvedQuestion: string | null;
  approvedAnswer: string | null;
  sortOrder: number;
  createdAt: string;
};

// One stored receptionist conversation (Task 1704). Only finished ("done")
// anonymous chats are returned by the list endpoint — lead-captured ones ride
// the lead, active ones are still in progress.
export type ReceptionistConversation = {
  id: number;
  userId: string;
  leadId: number | null;
  conversationId: string;
  messages: { role: "user" | "assistant"; content: string }[];
  status: "active" | "done";
  leadCaptured: boolean;
  notifiedAt: string | null;
  lastMessageAt: string;
  createdAt: string;
};

// One dismissed answer-gap question (Task 1733) — the owner marked "don't
// ask me about this again"; matching questions never re-flag.
export type ReceptionistGapDismissal = {
  id: number;
  userId: string;
  questionKey: string;
  question: string;
  createdAt: string;
};

export type BusinessFaq = {
  id: number;
  userId: string;
  question: string;
  answer: string;
  ctaType: string;
  ctaLabel: string | null;
  ctaTargetUrl: string | null;
  mediaUrl: string;
  mediaOriginalUrl: string;
  sortOrder: number;
  createdAt: string;
};

export type BusinessFaqInput = {
  question: string;
  answer: string;
  ctaType: string;
  ctaLabel: string | null;
  ctaTargetUrl: string | null;
  mediaUrl?: string;
  mediaOriginalUrl?: string;
};

export type SettingsResponse = {
  theme: string;
  fontSize: string;
  dashboardTitle: string;
  ownerName: string;
  customPrimaryColor: string | null;
  customSidebarColor: string | null;
  customBackgroundColor: string | null;
  defaultTaskDuration: string;
  defaultTaskType: string;
  defaultTaskFolder: string | null;
  calendarDefaultView: string;
  tasksDefaultView: string;
  flowOrder: string | null;
  adminTabOrder: string | null;
  calendarDayStart: number;
  calendarDayEnd: number;
  calendarWeekStart: string;
  calendarTimeZone: string;
  calendarShowPublicAvailability: boolean;
  showCompletedGoals: boolean;
  showClientGoals: boolean;
  showMedicalInformation: boolean;
  goalCheckInDays: number;
  goalTasksHiddenByDefault: boolean;
  pipelineWarnDays: number;
  dashboardWidgets: string | null;
  contactColumns: string | null;
  sidebarOrder: string | null;
  dashboardRightOrder: string | null;
  dashboardPillsOrder: string | null;
  dashboardMainOrder: string | null;
  dashboardWidgetConfigs: string | null;
  settingsTouched: string | null;
  contactTabOrder: string | null;
  contactPageTabOrder: string | null;
  contactBillingCardOrder: string | null;
  contactDetailsCardOrder: string | null;
  contactProgressCardOrder: string | null;
  contactMessagesCardOrder: string | null;
  analyticsSectionOrder: string | null;
  analyticsSelectedKpiOrder: string | null;
  analyticsAllTimeKpiOrder: string | null;
  analyticsCollapsedSections: string | null;
  onboardingCompleted: boolean;
  onboardingIntake?: import("@shared/setupIntake").SetupIntake | null;
  onboardingListsConfirmed: boolean;
  dashboardAccentMode: string;
  onboardingGoalDismissed: boolean;
  dashboardWelcomeDismissed: boolean;
  dismissedDuplicatesNoticeHidden: boolean;
  signatureEnabled: boolean;
  signatureSalutation: string;
  signatureName: string;
  signatureShowBusinessName: boolean;
  signatureShowWebsite: boolean;
  signatureShowPhone: boolean;
  signatureShowAddress: boolean;
  signatureShowSocials: boolean;
  signatureTitle: string;
  signatureExtra: string;
  signatureOrder: string;
  purchaseReason: string | null;
  includeCompletedTasks: boolean;
  // Pipeline-specific average deal value in cents (Task 2484). Saved per-owner
  // and independent of the onboarding baseline — editing one never writes the
  // other. Null = not set (totals hidden on the pipeline board).
  pipelineDealValueCents: number | null;
  // Server-managed (never PATCHable): true while the paid onboarding video
  // generation is deferred for an unpaid signup — set when they finish the
  // wizard unpaid, cleared the moment the subscription mirror flips active
  // and the deferred generation is claimed. The dashboard uses it to show
  // "your welcome video is being created" right after they subscribe.
  pendingOnboardingVideos: boolean;
  pendingOnboardingVideoFailures: number;
};

export type MergeTagOption = {
  key: string;
  label: string;
  value: string;
  editable: boolean;
  contactDriven?: boolean;
  custom?: boolean;
};

// One direction of a contact link, flattened with the related contact's
// display fields. Mirror of the server's ContactLinkView.
export type ContactLinkView = {
  id: number;
  relatedLeadId: number;
  label: string | null;
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  email: string | null;
};

// Clerk session-token getter, injected once from <AuthInit> after the Clerk
// provider mounts. The SPA and API are same-origin so Clerk's session cookie
// already authenticates most requests, but we also send the JWT as a Bearer
// token (the canonical Clerk SPA→API path) so auth is robust regardless of
// cookie quirks. Stays null until set, and on the public/marketing surfaces
// where Clerk never mounts, so those pages issue plain unauthenticated fetches.
let clerkGetToken: (() => Promise<string | null>) | null = null;
export function setClerkGetToken(fn: (() => Promise<string | null>) | null) {
  clerkGetToken = fn;
}
export async function authHeader(): Promise<Record<string, string>> {
  if (!clerkGetToken) return {};
  try {
    const token = await clerkGetToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  const workspaceHeaders = isWorkspaceWrite(options?.method ?? "GET")
    ? expectedWorkspaceHeaders(url)
    : {};
  const headers = new Headers({
    "Content-Type": "application/json",
    ...workspaceHeaders,
  });
  for (const [key, value] of Object.entries(await authHeader())) headers.set(key, value);
  new Headers(options?.headers).forEach((value, key) => headers.set(key, value));

  const res = await fetch(url, {
    ...options,
    headers,
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const error = new Error(body.error || res.statusText) as Error & {
      status?: number;
      code?: string;
      field?: string;
    };
    error.status = res.status;
    if (body.code) error.code = body.code;
    if (typeof body.field === "string") error.field = body.field;
    throw error;
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// Generic client for the per-user, sort-ordered "Website" content entities
// (services / gallery / team / testimonials). Mirrors the server registrar.
function contentApi<T, I>(base: string) {
  return {
    list: () => fetchJSON<T[]>(`/api/${base}`),
    create: (data?: Partial<I>) =>
      fetchJSON<T>(`/api/${base}`, { method: "POST", body: JSON.stringify(data ?? {}) }),
    update: (id: number, data: Partial<I>) =>
      fetchJSON<T>(`/api/${base}/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    delete: (id: number) =>
      fetchJSON<{ success: boolean }>(`/api/${base}/${id}`, { method: "DELETE" }),
    reorder: (orderedIds: number[]) =>
      fetchJSON<T[]>(`/api/${base}/reorder`, { method: "POST", body: JSON.stringify({ orderedIds }) }),
  };
}

// Unified website-slide visitor actions (Task 3619). These are server-owned
// slide fields; keep their client contract explicit without editing the shared
// schema in this task.
export type BusinessSlideActionFields = {
  actionType: "none" | "button" | "session" | "form";
  actionSessionTypeId: number | null;
  actionCampaignId: number | null;
};

export type DashboardInitResponse = {
  tasks: Task[];
  goals: Goal[];
  leads: Lead[];
  pipelineStages: PipelineStage[];
  sources: Source[];
  directions: Direction[];
  channels: Channel[];
  contactTypes: Relationship[];
  settings: SettingsResponse;
  emailConfig: SettingsEmailConfig;
};

// Time-fair next-action suggestion for a breakdown row. Mirrors the server's
// Suggestion in server/analyticsSuggestions.ts. `label` is the Title Case pill
// text; `reason` is the plain-language hover/expand explanation.
// One Trackable Asset row. All-time click and lead counts remain available in
// the Flow manager even though Analytics no longer has a separate asset report.
export type TrackingLinkRow = {
  id: number;
  code: string;
  name: string;
  destination: string;
  destinationLabel: string;
  source: "manual" | "email" | "sms" | "asset" | "trackable_asset" | string;
  assetType?: "link" | "qr" | string;
  archived: boolean;
  trackingEnabled: boolean;
  createdAt: string;
  url: string;
  clicks: number;
  leads: number;
  leadConversionRate: number;
  won: number;
  revenueCents: number;
  // True when a Stripe-matched contact's live lookup failed — the revenue
  // sum is partial, rendered as "(partial)"/"Unavailable", never an exact $.
  // Optional for back-compat with cached responses.
  revenueIncomplete?: boolean;
  salesConversionRate: number;
  // ISO timestamp of the most recent click on this link; null if never clicked.
  lastClickedAt: string | null;
};

export type AnalyticsSuggestionAction =
  | "scale_up"
  | "drive_traffic"
  | "tighten_follow_up"
  | "consider_pausing"
  | "gathering_data";
export type AnalyticsSuggestion = {
  action: AnalyticsSuggestionAction;
  label: string;
  reason: string;
  dataSays?: string;
  nextMove?: string;
  evidence?: {
    row: { leads: number; closeRate: number; revenueCents: number };
    benchmark: { leads: number; closeRate: number; revenueCents: number } | null;
  };
};

// Per-campaign analytics bucket (first-touch attribution). Mirrors the
// server's CampaignBucket in server/campaignAnalytics.ts. Revenue values are
// integer cents.
export type CampaignBucket = {
  campaignId: number | null;
  name: string;
  // Leads attributed to this campaign that entered the pipeline (have a stage
  // OR a Won/Lost outcome) — matches how Best Sources / Best Channels count.
  leads: number;
  conversions: number;
  lost: number;
  closeRate: number;
  revenueCents: number;
  avgSaleAmountCents: number;
  revenuePerLeadCents: number;
  revenuePerLeadIncomplete?: boolean;
  hasClosed: boolean;
  // Real Stripe revenue from matched subscriber contacts (Task 1919) —
  // separate from revenueCents (typed Sale Amounts). Optional for back-compat.
  realSubscribers?: number;
  realMrrCents?: number;
  realCollectedCents?: number;
  // One-time (non-subscription) Stripe payments, net of refunds (Task 1923).
  realOneTimeCents?: number;
  // True = realCollectedCents/realOneTimeCents are partial sums (a live lookup failed).
  realCollectedIncomplete?: boolean;
  // Manual recurring revenue (Task 1924): user-typed assumed-recurring
  // entries, projected to date + as a monthly rate. Estimates — separate
  // from both revenueCents and the real* Stripe figures.
  manualRecurringCents?: number;
  manualMrrCents?: number;
  // Revenue compatibility fields: Stripe payments plus applicable manually
  // entered one-time and recurring revenue. combinedIncomplete=true means
  // Stripe data is missing, while known manual revenue remains in the sum.
  combinedRevenueCents?: number;
  combinedIncomplete?: boolean;
  // Time-fair recommended next action. Absent on the Vault campaign-stats path.
  suggestion?: AnalyticsSuggestion;
};

// ── Subscriber payment status (Task #1543) ─────────────────────────────────
// Read-only Stripe MIRROR facts for the admin-only ContactCard Subscription
// panel. `subscribed: false` = this contact isn't a mirrored subscriber.
export type ContactSubscription =
  | { subscribed: false }
  | {
      subscribed: true;
      contactUpdated: boolean;
      status: string | null;
      active: boolean;
      lifecycleStage: string | null;
      priceCents: number | null;
      priceCurrency: string | null;
      priceInterval: string | null;
      priceIntervalCount: number | null;
      currentPeriodEnd: string | null;
      cancelAt: string | null;
      signupAt: string | null;
    };

// Billing history rows for the admin-only Billing History panel (Task 1916).
export type ContactBillingInvoice = {
  id: string;
  number: string | null;
  createdAt: string;
  totalCents: number;
  currency: string;
  status: string; // paid | open | void | uncollectible | refunded
  hostedInvoiceUrl: string | null;
};
// Standalone one-time payments (Task 1923): succeeded charges that never
// touched an invoice — payment links, one-time checkout.
export type ContactBillingPayment = {
  id: string;
  createdAt: string;
  amountCents: number;
  currency: string;
  status: string; // paid | refunded | partially refunded
  description: string | null;
  receiptUrl: string | null;
};
// One manual one-off payment row for a contact (itemized Sale Amount).
export type LeadPaymentEntry = {
  id: number;
  leadId: number;
  amountCents: number;
  paidDate: string | null;
  note: string | null;
  createdAt: string;
};

export type ContactImportantDate = {
  id: number;
  userId: string;
  leadId: number;
  systemKey: string | null;
  month: number;
  day: number;
  year: number | null;
  label: string;
  reminderEnabled: boolean;
  reminderDaysBefore: number | null;
  recurrenceUnit: "day" | "week" | "month" | "year" | null;
  recurrenceInterval: number | null;
  reminderTaskId: number | null;
  createdAt: string;
  updatedAt: string;
};
export type ClientGoalStatus = "active" | "achieved" | "paused";
export type ClientGoalProgressEntry = {
  id: number;
  goalId: number;
  note: string;
  entryDate: string; // "YYYY-MM-DD"
  createdAt: string;
};
// QR door check-in (Task 2107).
export type CheckinOverview = {
  url: string;
  displayName: string;
  heading: string;
  buttonText: string;
  contactMessage: string;
  thankYouTitle: string;
  welcomeBackMessage: string;
  targetVisits: number;
  rewardText: string;
  notifyOnReward: boolean;
  notifyByEmail: boolean;
  notifyBySms: boolean;
  notificationEmails: string[];
  notificationMobile: string;
  notificationMobiles: string[];
  emailSubject: string;
  emailBody: string;
  textBody: string;
  emailOptions: CheckinNotificationEmailOptions;
  visitsThisWeek: number;
  today: {
    id: number;
    leadId: number | null;
    name: string;
    phone: string;
    className: string | null;
    wasNewLead: boolean;
    at: string;
    // Active goal + latest note for the pre-class cheat sheet (Task 2108).
    goal: { title: string; latestNote: string | null; latestNoteDate: string | null; daysSince: number | null } | null;
  }[];
  rewardsReached: { leadId: number; name: string; count: number }[];
};

export type LeadCheckinEntry = {
  id: number;
  localDate: string; // "YYYY-MM-DD" owner-local
  className: string | null;
  at: string;
};

// Attendance drift row (Task 2147): a client quiet vs their OWN cadence.
export type AttendanceAttentionItem = {
  leadId: number;
  name: string;
  cadenceLabel: string; // "about 3x/week" / "about every 10 days"
  typicalGapDays: number;
  daysSinceLastVisit: number;
  lastVisitDate: string; // "YYYY-MM-DD" owner-local
  /** Set when the owner sent an outbound email/SMS within the suppress window (Task 2404). */
  daysSinceMessaged?: number;
};
export type AttendanceAttentionResponse = { items: AttendanceAttentionItem[] };

export type PipelineStaleItem = {
  leadId: number;
  name: string;
  daysSince: number;
  warnDays: number;
  agingState: "red";
};
export type ClientGoalEntry = {
  id: number;
  leadId: number;
  title: string;
  targetDate: string | null; // "YYYY-MM-DD"
  status: ClientGoalStatus;
  achievedAt: string | null;
  createdAt: string;
  updatedAt: string;
  progress: ClientGoalProgressEntry[];
};

export type ClientGoalCheckInEntry = {
  goalId: number;
  leadId: number;
  leadName: string;
  goalTitle: string;
  lastActivityDate: string; // "YYYY-MM-DD"
  daysSince: number;
};
export type ContactBillingHistory = {
  subscribed: boolean;
  configured: boolean;
  invoices: ContactBillingInvoice[];
  payments: ContactBillingPayment[];
};

// ── BYO Stripe (Task 1917) ──────────────────────────────────────────────────
// A user's OWN connected Stripe account (read-only OAuth). Status for the
// settings card, per-contact match/link facts, and manual-link customer search.
export type StripeConnectStatus = {
  available: boolean;
  connected: boolean;
  accountId: string | null;
};
export type ContactStripeLink =
  | { connected: false; matched?: false }
  | { connected: true; matched: false }
  | {
      connected: true;
      matched: true;
      matchSource: "manual" | "email";
      customerId: string;
    };
// Current-subscription summary for a matched contact (Task 1928): the BYO
// counterpart of the admin Subscription panel — plan price, interval, next
// renewal, active/canceled, read live from the connected account.
export type ContactClientSubscriptionItem = {
  priceCents: number | null;
  currency: string;
  interval: string | null; // month | year | week | day
  intervalCount: number;
  quantity: number;
  nickname: string | null;
};
export type ContactClientSubscription = {
  id: string;
  status: string; // active | trialing | past_due | unpaid | paused | canceled
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  cancelAt: string | null;
  canceledAt: string | null;
  items: ContactClientSubscriptionItem[];
};
export type ContactClientSubscriptions = {
  configured: boolean;
  matched: boolean;
  subscriptions: ContactClientSubscription[];
};

export type ContactRealRevenueFigures = {
  configured: boolean;
  matched: boolean;
  /** True when any Stripe lookup failed — figures are null, render "Unavailable". */
  lookupFailed: boolean;
  mrrCents: number | null;
  collectedCents: number | null;
  oneTimeCents: number | null;
};
export type StripeConnectCustomer = {
  id: string;
  email: string | null;
  name: string | null;
};
// Account-wide match review (Task 1930): every contact that resolved to a
// Stripe customer, plus every Stripe customer no contact matched.
export type StripeConnectMatchedContact = {
  leadId: number;
  leadName: string;
  leadEmail: string | null;
  customerId: string;
  matchSource: "manual" | "email";
  customerName: string | null;
  customerEmail: string | null;
  mrrCents: number;
};
export type StripeConnectUnmatchedCustomer = {
  customerId: string;
  name: string | null;
  email: string | null;
  mrrCents: number;
};
export type StripeConnectMatches = {
  connected: boolean;
  matched: StripeConnectMatchedContact[];
  unmatchedCustomers: StripeConnectUnmatchedCustomer[];
};
// Bulk create-contacts summary (Task 1938).
export type StripeConnectBulkCreateResult = {
  ok: boolean;
  total: number;
  created: number;
  failed: Array<{ customerId: string; label: string; error: string }>;
};

// Admin real-collected-revenue report (Task 1918). Live Stripe aggregation,
// cached 10 min server-side. All money figures are integer cents.
export type AdminRevenueMonth = {
  month: string;         // "YYYY-MM"
  collectedCents: number;
  refundedCents: number;
  failedCents: number;
  invoiceCount: number;
};
export type AdminRevenueSubscriber = {
  userId: string;
  stripeCustomerId: string;
  leadId: number | null;
  /** CRM contact name or business name; null → fall back to email, then id. */
  displayName: string | null;
  email: string | null;
  lifetimeCollectedCents: number;
};
export type AdminChurnedMrrMonth = {
  month: string;
  churnedCents: number;
};
export type AdminAnnualCancellation = {
  stripeSubscriptionId: string;
  amountCents: number;
  canceledAt: string;
};
export type AdminRevenueReport =
  | {
      configured: true;
      /** "YYYY-MM" of the prior calendar month at the server's clock. */
      priorMonth: string;
      months: AdminRevenueMonth[];
      subscribers: AdminRevenueSubscriber[];
      churnedMrr: AdminChurnedMrrMonth[];
      annualCancellations: AdminAnnualCancellation[];
      asOf: string;
    }
  | { configured: false };

// Admin Billing tab roll-up (server-computed, cents end-to-end).
export type BillingReportPerson = {
  userId: string;
  displayName: string | null;
  email: string | null;
  leadId: number | null;
};
export type BillingReport = {
  paying: { total: number; monthly: number; yearly: number };
  leads: number;
  pastUsers: number;
  monthlyRevenueCents: number;
  upcomingYearlyRenewals: (BillingReportPerson & {
    renewsAt: string;
    amountCents: number | null;
    currency: string | null;
  })[];
  needsAttention: (BillingReportPerson & {
    status: string;
    amountCents: number | null;
    currency: string | null;
    interval: string | null;
  })[];
  pendingCancellations: (BillingReportPerson & { cancelAt: string })[];
};

// Owner-facing history for platform-to-owner Digital Receptionist SMS alerts.
// Kept client-local until the endpoint's contract is stable across clients.
export type ReceptionistTextAlertHistoryStatus =
  | "requested"
  | "queued"
  | "accepted"
  | "sending"
  | "sent"
  | "delivered"
  | "failed"
  | "undelivered"
  | "unconfirmed";
export type ReceptionistTextAlertHistoryItem = {
  id: string;
  leadName: string;
  createdAt: string;
  destinationMasked: string;
  status: ReceptionistTextAlertHistoryStatus;
  failureReason: string | null;
  contactUrl: string | null;
  conversationUrl: string | null;
};
export type ReceptionistTextAlertHistoryPage = {
  items: ReceptionistTextAlertHistoryItem[];
  hasMore: boolean;
};

export const api = {
  contactFields: contactFieldsApi,
  freeConsultation: {
    get: () =>
      fetchJSON<{ tab: WebsiteTab | null; slide: BusinessSlide | null; showOnWebsite: boolean }>(
        "/api/free-consultation-flow",
      ),
    createSlide: () =>
      fetchJSON<{ slide: BusinessSlide }>("/api/free-consultation-flow/slide", {
        method: "POST",
      }),
  },
  referralFlow: {
    get: () => fetchJSON<ReferralFlowSettings>("/api/referral-flow"),
    update: (data: ReferralFlowPatch) =>
      fetchJSON<ReferralFlowSettings>("/api/referral-flow", { method: "PATCH", body: JSON.stringify(data) }),
    askStatus: (leadId: number) =>
      fetchJSON<{ cooldownActive: boolean; lastSentAt: string | null }>(`/api/referral-flow/ask-status/${leadId}`),
  },
  testimonialRequestFlow: {
    getQr: () => fetchJSON<import("@shared/testimonialCollectionQr").TestimonialCollectionQr>("/api/testimonial-request-flow/qr"),
    get: () => fetchJSON<TestimonialRequestFlowSettings>("/api/testimonial-request-flow"),
    getUrl: () => fetchJSON<{ url: string; pageId: number | null; published: boolean }>("/api/testimonial-request-flow/url"),
    createPage: () => fetchJSON<{ url: string; pageId: number | null; published: boolean }>("/api/testimonial-request-flow/page", { method: "POST" }),
    update: (data: TestimonialRequestFlowPatch) =>
      fetchJSON<TestimonialRequestFlowSettings>("/api/testimonial-request-flow", {
        method: "PATCH",
        body: JSON.stringify(data),
        // Let small in-flight settings edits finish during document navigation.
        // Larger attachments exceed browsers' keepalive quota; the editor's
        // tenant-isolated pending draft recovers those on reopening.
        keepalive: new TextEncoder().encode(JSON.stringify(data)).byteLength < 60_000,
      }),
  },
  dashboard: {
    init: () => fetchJSON<DashboardInitResponse>("/api/dashboard/init"),
    schedule: (date: string) =>
      fetchJSON<DashboardScheduleItem[]>(`/api/dashboard/schedule?date=${encodeURIComponent(date)}`),
  },
  onboarding: {
    intake: {
      get: () => fetchJSON<import("@shared/setupIntake").SetupIntake>("/api/onboarding/intake"),
      adminQueue: () => fetchJSON<Array<{ userId: string; businessName: string | null; submittedAt: string; readyAt: string | null; emailStatus: string | null }>>("/api/admin/setup-queue"),
      retryEmail: (userId: string, kind: import("@shared/setupIntake").SetupEmailKind) =>
        fetchJSON<import("@shared/setupIntake").SetupIntake>(`/api/admin/onboarding-intake/${encodeURIComponent(userId)}/email/retry`, { method: "POST", body: JSON.stringify({ kind }) }),
      save: (data: import("@shared/setupIntake").SetupIntakeData, revision: number) =>
        fetchJSON<import("@shared/setupIntake").SetupIntake>("/api/onboarding/intake", { method: "PUT", body: JSON.stringify({ data, revision }) }),
      submit: (revision: number) => fetchJSON<import("@shared/setupIntake").SetupIntake>("/api/onboarding/intake/submit", { method: "POST", body: JSON.stringify({ revision }) }),
      upload: async (file: File, purpose?: "customer-import") => {
        const response = await fetch("/api/onboarding/intake/files", {
          method: "POST", credentials: "include",
          headers: { ...(await authHeader()), "Content-Type": "application/octet-stream", "X-File-Name": encodeURIComponent(file.name), ...(purpose ? { "X-Setup-Purpose": purpose } : {}) },
          body: file,
        });
        if (!response.ok) throw new Error((await response.json()).error || "Your file could not be saved. Please try again.");
        return response.json() as Promise<import("@shared/setupIntake").SetupIntake>;
      },
      removeFile: (id: string) => fetchJSON<import("@shared/setupIntake").SetupIntake>(`/api/onboarding/intake/files/${encodeURIComponent(id)}`, { method: "DELETE" }),
      adminGet: (userId: string) => fetchJSON<{ intake: import("@shared/setupIntake").SetupIntake; profile: BusinessProfileResponse; baseline: BaselineResponse }>(`/api/admin/onboarding-intake/${encodeURIComponent(userId)}`),
      adminUpdate: (userId: string, revision: number, questions: string, ready = false) => fetchJSON<import("@shared/setupIntake").SetupIntake>(`/api/admin/onboarding-intake/${encodeURIComponent(userId)}`, { method: "PUT", body: JSON.stringify({ revision, questions, ready }) }),
    },
    // Kick off the three default presenter videos at the end of onboarding.
    // Best-effort + idempotent on the server; renders run in the background.
    generateDefaultVideos: () =>
      fetchJSON<{ started: string[]; skipped: string[] }>(
        "/api/onboarding/generate-default-videos",
        { method: "POST" },
      ),
    // AI-draft the hero copy (up to 3 headline options + message) plus up to 3
    // presenter welcome-script options from the owner's Basics (business name +
    // description). Option order is pain-point / company-specific / general.
    // Used by the Avatar step's pickers; always returns usable copy
    // (deterministic single-option fallbacks when OpenAI isn't configured).
    draftHero: () =>
      fetchJSON<{
        headlines: string[];
        subcopy: string;
        welcomeScripts: string[];
        // Fingerprint of the Basics (name::description) as of the draft run.
        // Written server-side; included so the client cache can sync immediately.
        heroDraftBasicsSnapshot: string;
      }>(
        "/api/onboarding/draft-hero",
        { method: "POST" },
      ),
    // "Pricing" step: saves what the owner writes as an APPROVED receptionist
    // knowledgebase pricing entry. Blank save deletes the entry.
    pricing: {
      // Returns { text, seq } — seq is the server's stored save_seq and is
      // used by PricingStep to initialise saveSeqRef above the last-committed
      // value on every remount so the ordering token is monotonic across tabs.
      get: () => fetchJSON<{ text: string; seq: number }>("/api/onboarding/pricing"),
      // seq is a monotonic client counter that lets the server reject stale
      // delayed requests (see PUT /api/onboarding/pricing and
      // storage.upsertOnboardingPricingEntry for the ordering guard).
      save: (text: string, seq: number) =>
        fetchJSON<{ text: string; seq: number }>("/api/onboarding/pricing", {
          method: "PUT",
          body: JSON.stringify({ text, seq }),
        }),
    },
  },
  // Manual "Try again now" for the deferred onboarding welcome-video
  // generation (shown on the snag notice after repeated failures). Atomic
  // server-side claim makes double-clicks safe.
  onboardingRetryDeferredVideos: () =>
    fetchJSON<{ ok: boolean }>("/api/onboarding/retry-deferred-videos", { method: "POST" }),
  aiVideo: {
    setPendingName: (id: number, name: string, legacy = false) =>
      fetchJSON<{ names: Record<string, string> }>(`/api/ai-video/${id}/pending-name`, {
        method: "POST", body: JSON.stringify({ name, legacy }),
      }),
    list: () => fetchJSON<AiVideoJobPayload[]>("/api/ai-video"),
    get: (id: number) => fetchJSON<AiVideoJob>(`/api/ai-video/${id}`),
    quota: () => fetchJSON<AiVideoQuota>("/api/ai-video/quota"),
    create: (data: AiVideoCreateInput) =>
      fetchJSON<AiVideoJob>("/api/ai-video", { method: "POST", body: JSON.stringify(data) }),
    // Re-run a generated job's EXACT same generation (presenter + script) from
    // the persisted job. Used by the "Generate Again With Backdrop" retry when
    // the studio backdrop couldn't be applied.
    retry: (id: number) =>
      fetchJSON<AiVideoJob>(`/api/ai-video/${id}/retry`, { method: "POST" }),
    // Rename a library clip (label is the only editable field).
    update: (id: number, data: { label: string | null }) =>
      fetchJSON<AiVideoJob>(`/api/ai-video/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    delete: (id: number) => fetchJSON<{ ok: boolean }>(`/api/ai-video/${id}`, { method: "DELETE" }),
    // Upload an already-finished video file. Sends raw bytes (not base64) so
    // large files don't bloat the request; purpose/label travel as query params.
    upload: async (
      file: File,
      opts?: { purpose?: string | null; label?: string | null },
    ): Promise<AiVideoJob> => {
      const headers: Record<string, string> = {
        "Content-Type": file.type || "application/octet-stream",
        ...(await authHeader()),
      };
      const params = new URLSearchParams();
      if (opts?.purpose) params.set("purpose", opts.purpose);
      if (opts?.label) params.set("label", opts.label);
      const qs = params.toString();
      const res = await fetch(`/api/ai-video/upload${qs ? `?${qs}` : ""}`, {
        method: "POST",
        headers,
        credentials: "include",
        body: file,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(body.error || res.statusText);
      }
      return res.json();
    },
  },
  presenter: {
    // (The uploaded-video digital-twin builder — build/buildStatus/remove +
    // the /api/presenter/build routes — was removed July 2026, and the unused
    // ready-made stock-avatar picker — pickStock/clearStock +
    // /api/presenter/stock — was removed July 2026 with HeyGen's v2 sunset;
    // the avatar library methods below are the only presenter surface.)
    getVoiceUpgrade: () =>
      fetchJSON<{ eligibleAvatarIds: number[]; dismissed: boolean; pending: boolean }>(
        "/api/presenter/voice-upgrade",
      ),
    dismissVoiceUpgrade: () =>
      fetchJSON<{ ok: true }>("/api/presenter/voice-upgrade/dismiss", {
        method: "POST",
        body: JSON.stringify({}),
      }),
    updateCustomVoice: (
      id: number,
      input: { voiceSampleUrls: string[]; consent: true; requestId: string },
    ) =>
      fetchJSON<{ ok: true }>(`/api/presenter/custom/library/${id}/voice`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    // Build an avatar of yourself or a staff member from an uploaded photo
    // (imageUrl in the media library). Voice defaults to built-in HeyGen
    // TTS; you may opt into a clone ("clone", voiceSampleUrl). Fire-and-forget —
    // returns the profile in the "creating" state; poll videoCustomStatus.
    buildCustom: (input: {
      // Photo-only: an avatar of yourself or a staff member. (The AI
      // "describe a spokesperson" path was removed.)
      imageUrl?: string;
      // Optional owner-given name; when blank the library shows a generic
      // "Photo Avatar" label (renameable inline in the library later).
      name?: string;
      // Required consent: permission to use the person's likeness + voice, and
      // that The Coffey Company (InControl) isn't liable for how it's used.
      consent?: boolean;
      voiceKind?: "builtin" | "clone";
      // Legacy single-sample field (kept for back-compat callers/tests).
      voiceSampleUrl?: string;
      // Multi-take clone samples (Task #1548): up to 3 polished sample URLs,
      // all fed to one ElevenLabs clone call for better likeness.
      voiceSampleUrls?: string[];
      // "original" (default) keeps the source background; "professional" bakes in
      // the charcoal studio backdrop; "custom" composites the person onto
      // customBackgroundUrl.
      background?: "original" | "professional" | "custom";
      // Which professional plate to bake in when background is "professional"
      // (Task #1417): charcoal (default), white, or the owner's accent plate.
      professionalVariant?: "charcoal" | "white" | "accent";
      // Required when background is "custom": an owner-uploaded background still
      // already in the media library.
      customBackgroundUrl?: string;
    }) =>
      fetchJSON<BusinessProfileResponse>("/api/presenter/custom", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    // Deactivate the active custom avatar (keeps it in the library) and fall back
    // to the digital twin.
    clearCustom: () =>
      fetchJSON<BusinessProfileResponse>("/api/presenter/custom", { method: "DELETE" }),
    // The owner's re-usable custom-avatar library (Task #656 follow-up). Drives
    // the dashboard "Re-Use an Avatar You Already Made" picker.
    listCustom: () => fetchJSON<CustomAvatar[]>("/api/presenter/custom/library"),
    // Re-use a saved avatar as the active one (no HeyGen rebuild). Returns the
    // updated business profile.
    selectCustom: (id: number) =>
      fetchJSON<BusinessProfileResponse>("/api/presenter/custom/select", {
        method: "POST",
        body: JSON.stringify({ id }),
      }),
    // Delete a saved avatar from the library (removes its cloned voice + row).
    deleteCustom: (id: number) =>
      fetchJSON<{ ok: boolean }>(`/api/presenter/custom/library/${id}`, { method: "DELETE" }),
    // Rename a saved avatar (inline autosave in the library). Starters can't be
    // renamed (server 403s).
    renameCustom: (id: number, name: string) =>
      fetchJSON<CustomAvatar>(`/api/presenter/custom/library/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      }),
    // Label ONE look within a saved avatar's group (Task #1196). Blank clears
    // the label back to the positional default ("Look 1/2/…").
    setLookLabel: (id: number, lookLabel: string) =>
      fetchJSON<CustomAvatar>(`/api/presenter/custom/library/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ lookLabel }),
      }),
    // Make one ready look the group's card thumbnail in the library
    // (Task #1196). The server clears the flag on every sibling look.
    setCardLook: (id: number) =>
      fetchJSON<CustomAvatar>(`/api/presenter/custom/library/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ cardLook: true }),
      }),
    // Add an extra look (new outfit/setting, same person) to a saved avatar
    // (Task #1180). Returns the new sibling row in status "training"; poll the
    // library until it flips to "pending", then preview + approve/discard.
    // `background` (optional) picks this look's backdrop — "professional"
    // (charcoal plate), "original" (keep the photo's own background), or
    // "custom" (+ backgroundUrl, an uploaded still). Omitted → mirror the base
    // avatar's treatment.
    addLook: (
      id: number,
      input: {
        kind: "photo";
        imageUrl: string;
        background?: "professional" | "original" | "custom";
        // Which professional plate when background is "professional" (Task #1417).
        professionalVariant?: "charcoal" | "white" | "accent";
        backgroundUrl?: string;
        // Required likeness consent for THIS look's photo (Task 1995) —
        // server-enforced and persisted as an append-only attestation record.
        consent: boolean;
      },
    ) =>
      fetchJSON<CustomAvatar>(`/api/presenter/custom/library/${id}/look`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    // Approve a trained look (status pending → ready) so it becomes selectable.
    approveLook: (id: number) =>
      fetchJSON<CustomAvatar>(`/api/presenter/custom/library/${id}/approve`, {
        method: "POST",
      }),
    // Reorder the looks within a saved avatar's group (Task #1222). `lookOrder`
    // must list EVERY sibling row id exactly once, in the desired display
    // order; the server rewrites sortOrder 1..n. Returns the group's rows.
    reorderLooks: (id: number, lookOrder: number[]) =>
      fetchJSON<CustomAvatar[]>(`/api/presenter/custom/library/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ lookOrder }),
      }),
    // Reorder the custom PEOPLE (avatars) in the library grid (Task #1231).
    // `personOrder` lists one row id per person (any row of the group),
    // covering every custom person exactly once, in the desired display order;
    // the server rewrites personSortOrder 1..n across each whole group.
    // Returns all the owner's custom rows in the new order.
    reorderPeople: (id: number, personOrder: number[]) =>
      fetchJSON<CustomAvatar[]>(`/api/presenter/custom/library/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ personOrder }),
      }),
  },
  tasks: {
    getAll: () => fetchJSON<Task[]>("/api/tasks"),
    getByDate: (date: string) => fetchJSON<Task[]>(`/api/tasks?date=${date}`),
    getByDay: (day: string) => fetchJSON<Task[]>(`/api/tasks/${day}`),
    create: (data: Partial<Task> & { title: string }) =>
      fetchJSON<Task>("/api/tasks", { method: "POST", body: JSON.stringify(data) })
        .then((task) => {
          notifyTaskChanged();
          return task;
        }),
    update: (id: number, data: Partial<Task>) =>
      fetchJSON<Task>(`/api/tasks/${id}`, { method: "PATCH", body: JSON.stringify(data) })
        .then((task) => {
          notifyTaskChanged();
          return task;
        }),
    delete: (id: number) =>
      fetchJSON<void>(`/api/tasks/${id}`, { method: "DELETE" })
        .then((result) => {
          notifyTaskChanged();
          return result;
        }),
    // `folder` per item supports atomic cross-folder drags (move + re-slot in one transaction).
    reorder: (items: { id: number; sortOrder: number; folder?: string | null }[]) =>
      fetchJSON<{ ok: boolean }>("/api/tasks/reorder", { method: "PATCH", body: JSON.stringify(items) }),
  },
  attendanceFlow: {
    get: () => fetchJSON<import("@shared/attendanceFlow").AttendanceFlowSettings>("/api/attendance-flow"),
    update: (patch: import("@shared/attendanceFlow").AttendanceFlowPatch) =>
      fetchJSON<import("@shared/attendanceFlow").AttendanceFlowSettings>("/api/attendance-flow", { method: "PATCH", body: JSON.stringify(patch), keepalive: !patch.email || Object.keys(patch.email).every((key) => key === "enabled") }),
  },
  pastClientReactivationFlow: {
    get: () => fetchJSON<PastClientReactivationFlowSettings>("/api/past-client-reactivation-flow"),
    update: (patch: PastClientReactivationFlowPatch) =>
      fetchJSON<PastClientReactivationFlowSettings>("/api/past-client-reactivation-flow", {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
  },
  leads: {
    getAll: (view?: "all") => fetchJSON<Lead[]>(view === "all" ? "/api/leads?view=all" : "/api/leads"),
    // Single-contact fetch for the profile page deep link (Task 1915).
    // Resolves null on 404 so "not found" renders friendly, never throws.
    getById: (id: number) =>
      fetchJSON<Lead>(`/api/leads/${id}`).catch((err: any) => {
        if (err?.status === 404) return null;
        throw err;
      }) as Promise<Lead | null>,
    create: (data: { handle?: string; method?: string; stage?: string; firstName?: string; lastName?: string; email?: string; phone?: string; company?: string; website?: string; direction?: string; channel?: string; notes?: string }) =>
      fetchJSON<Lead>("/api/leads", { method: "POST", body: JSON.stringify(data) }),
    update: (id: number, data: Partial<Lead>) =>
      fetchJSON<Lead>(`/api/leads/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    // Manual one-off payments (itemized Sale Amount, August 2026). The
    // server keeps leads.saleAmount = SUM(payments) on every mutation.
    getPayments: (leadId: number) => fetchJSON<LeadPaymentEntry[]>(`/api/leads/${leadId}/payments`),
    createPayment: (leadId: number, data: { amountCents: number; paidDate?: string | null; note?: string | null }) =>
      fetchJSON<LeadPaymentEntry>(`/api/leads/${leadId}/payments`, { method: "POST", body: JSON.stringify(data) }),
    updatePayment: (paymentId: number, data: { amountCents?: number; paidDate?: string | null; note?: string | null }) =>
      fetchJSON<LeadPaymentEntry>(`/api/leads/payments/${paymentId}`, { method: "PATCH", body: JSON.stringify(data) }),
    deletePayment: (paymentId: number) =>
      fetchJSON<void>(`/api/leads/payments/${paymentId}`, { method: "DELETE" }),
    // Client goals (Task 2096): free-text goal + dated progress log per
    // client contact (Goals tab in the profile's Activity group).
    // QR door check-in visit history (Task 2107, Visits tab).
    getCheckins: (leadId: number) =>
      fetchJSON<LeadCheckinEntry[]>(`/api/leads/${leadId}/checkins`),
    getClientGoals: (leadId: number) =>
      fetchJSON<ClientGoalEntry[]>(`/api/leads/${leadId}/client-goals`),
    createClientGoal: (leadId: number, data: { title: string; targetDate?: string | null; status?: ClientGoalStatus }) =>
      fetchJSON<ClientGoalEntry>(`/api/leads/${leadId}/client-goals`, { method: "POST", body: JSON.stringify(data) }),
    updateClientGoal: (goalId: number, data: { title?: string; targetDate?: string | null; status?: ClientGoalStatus }) =>
      fetchJSON<ClientGoalEntry>(`/api/leads/client-goals/${goalId}`, { method: "PATCH", body: JSON.stringify(data) }),
    deleteClientGoal: (goalId: number) =>
      fetchJSON<void>(`/api/leads/client-goals/${goalId}`, { method: "DELETE" }),
    createClientGoalProgress: (goalId: number, data: { note: string; entryDate?: string }) =>
      fetchJSON<ClientGoalProgressEntry>(`/api/leads/client-goals/${goalId}/progress`, { method: "POST", body: JSON.stringify(data) }),
    deleteClientGoalProgress: (entryId: number) =>
      fetchJSON<void>(`/api/leads/client-goals/progress/${entryId}`, { method: "DELETE" }),
    // Goal check-ins (Task 2099): dashboard list of clients whose active
    // goal has gone quiet; snooze hides a row for one staleness window.
    getClientGoalCheckIns: () =>
      fetchJSON<ClientGoalCheckInsResponse>("/api/client-goals/check-ins"),
    snoozeClientGoalCheckIn: (goalId: number) =>
      fetchJSON<{ snoozedUntil: string }>(`/api/leads/client-goals/${goalId}/check-in-snooze`, { method: "POST" }),
    // Pipeline card aging (Task 2485/2846): contacts at the configured warning
    // threshold for the Needs Attention dashboard card.
    getPipelineStale: () =>
      fetchJSON<{ items: PipelineStaleItem[]; warnDays: number }>("/api/leads/pipeline-stale"),
    snoozePipelineStale: (leadId: number, days?: number) =>
      fetchJSON<{ snoozedUntil: string }>(`/api/leads/${leadId}/pipeline-snooze`, {
        method: "POST",
        body: JSON.stringify(days !== undefined ? { days } : {}),
      }),
    // Important dates (Task 2333): anniversaries, renewals, and custom dates.
    getImportantDates: (leadId: number) =>
      fetchJSON<ContactImportantDate[]>(`/api/leads/${leadId}/important-dates`),
    createImportantDate: (leadId: number, data: CreateImportantDateInput) =>
      fetchJSON<ContactImportantDate>(`/api/leads/${leadId}/important-dates`, { method: "POST", body: JSON.stringify(data) }),
    updateImportantDate: (dateId: number, data: Partial<CreateImportantDateInput>) =>
      fetchJSON<ContactImportantDate>(`/api/leads/important-dates/${dateId}`, { method: "PATCH", body: JSON.stringify(data) }),
    deleteImportantDate: (dateId: number) =>
      fetchJSON<void>(`/api/leads/important-dates/${dateId}`, { method: "DELETE" }),
    delete: (
      id: number,
      taskAction?: "keep" | "remove",
      taskReassign?: import("./applyTaskReassign").TaskReassignWirePlan,
    ) =>
      fetchJSON<void>(`/api/leads/${id}${taskAction ? `?taskAction=${taskAction}` : ""}`, {
        method: "DELETE",
        body: taskReassign ? JSON.stringify({ taskReassign }) : undefined,
      }),
    bulkDelete: (
      ids: number[],
      taskAction?: "keep" | "remove",
      taskReassign?: import("./applyTaskReassign").TaskReassignWirePlan,
    ) =>
      fetchJSON<{ deleted: number }>("/api/leads/bulk-delete", { method: "POST", body: JSON.stringify({ ids, taskAction, taskReassign }) }),
    bulkUpdate: (ids: number[], updates: Record<string, any>, opts?: { taskAction?: "keep" | "remove" }) =>
      fetchJSON<{ updated: number }>("/api/leads/bulk-update", { method: "POST", body: JSON.stringify({ ids, updates, ...(opts?.taskAction ? { taskAction: opts.taskAction } : {}) }) }),
    customFields: (id: number) => fetchJSON<import("@shared/contactFields").ContactFieldValue[]>(`/api/leads/${id}/custom-fields`),
    merge: (primaryId: number, otherIds: number[], data: Partial<Lead>, customFieldResolutions?: Record<string, { leadId: number; revision: number }>) =>
      fetchJSON<{ merged: number; lead: Lead }>("/api/leads/merge", { method: "POST", body: JSON.stringify({ primaryId, otherIds, data, customFieldResolutions }) }),
    import: (contacts: Record<string, string | number>[], defaultCampaignId?: number | null) =>
      fetchJSON<{ imported: number; merged: number; skipped: number; errors: { row: number; error: string }[] }>("/api/leads/import", { method: "POST", body: JSON.stringify({ contacts, defaultCampaignId: defaultCampaignId ?? null }) }),
    checkDuplicates: (contacts: Record<string, string>[]) =>
      fetchJSON<{ matches: ImportDuplicateMatch[] }>("/api/leads/import/check-duplicates", { method: "POST", body: JSON.stringify({ contacts }) }),
    duplicates: () =>
      fetchJSON<{ groups: DuplicateGroup[] }>("/api/leads/duplicates"),
    dismissDuplicate: (ids: number[]) =>
      fetchJSON<{ ok: boolean }>("/api/leads/duplicates/dismiss", { method: "POST", body: JSON.stringify({ ids }) }),
    dismissedDuplicates: () =>
      fetchJSON<{ groups: DismissedDuplicateGroup[] }>("/api/leads/duplicates/dismissed"),
    undismissDuplicate: (ids: number[]) =>
      fetchJSON<{ ok: boolean }>("/api/leads/duplicates/undismiss", { method: "POST", body: JSON.stringify({ ids }) }),
    undismissAllDuplicates: () =>
      fetchJSON<{ ok: boolean }>("/api/leads/duplicates/undismiss-all", { method: "POST" }),
    reorder: (items: { id: number; sortOrder: number }[]) =>
      fetchJSON<{ ok: boolean }>("/api/leads/reorder", { method: "PATCH", body: JSON.stringify(items) }),
    setOutcome: (
      id: number,
      outcome: string | null,
      opts?: {
        taskAction?: "keep" | "remove";
        removeTaskIds?: number[];
        saleAmountCents?: number | null;
        taskReassign?: import("./applyTaskReassign").TaskReassignWirePlan;
      },
    ) =>
      fetchJSON<Lead>(`/api/leads/${id}/outcome`, {
        method: "POST",
        body: JSON.stringify({
          outcome,
          taskAction: opts?.taskAction,
          removeTaskIds: opts?.removeTaskIds,
          saleAmountCents: opts?.saleAmountCents,
          taskReassign: opts?.taskReassign,
        }),
      }),
    links: {
      getAll: (leadId: number) =>
        fetchJSON<ContactLinkView[]>(`/api/leads/${leadId}/links`),
      create: (leadId: number, relatedLeadId: number, label?: string | null) =>
        fetchJSON<ContactLinkView>(`/api/leads/${leadId}/links`, {
          method: "POST",
          body: JSON.stringify({ relatedLeadId, label: label ?? null }),
        }),
      updateLabel: (leadId: number, relatedLeadId: number, label: string | null) =>
        fetchJSON<ContactLinkView>(`/api/leads/${leadId}/links/${relatedLeadId}`, {
          method: "PATCH",
          body: JSON.stringify({ label }),
        }),
      delete: (leadId: number, relatedLeadId: number) =>
        fetchJSON<void>(`/api/leads/${leadId}/links/${relatedLeadId}`, { method: "DELETE" }),
    },
    analytics: (start?: string, end?: string, filters?: { source?: string; direction?: string; channel?: string }) => {
      const params = new URLSearchParams();
      if (start) params.set("start", start);
      if (end) params.set("end", end);
      if (filters?.source && filters.source !== "All") params.set("source", filters.source);
      if (filters?.direction && filters.direction !== "All") params.set("direction", filters.direction);
      if (filters?.channel && filters.channel !== "All") params.set("channel", filters.channel);
      const qs = params.toString();
      return fetchJSON<{
        // Pipeline leads only — contacts with a stage OR a Won/Lost outcome
        // (excludes does_not_apply and never-in-pipeline captures). Drives the
        // "Leads Generated" KPI and equals the sum of the breakdown Leads
        // columns.
        total: number;
        clients: number;
        won: number;
        lost: number;
        active: number;
        conversionRate: number;
        // All revenue values are integer cents; client formats to whole
        // dollars via formatCents() in AnalyticsView.
        totalRevenueCents: number;
        avgSaleAmountCents: number;
        revenuePerLeadCents: number;
        // Optional for compatibility with cached responses created before the
        // additive recorded-revenue Revenue per Lead calculation.
        revenuePerLeadIncomplete?: boolean;
        stageDistribution: Record<string, number>;
        // realSubscribers/realMrrCents/realCollectedCents (Task 1919): REAL
        // Stripe revenue from matched subscriber contacts in the bucket —
        // separate from revenueCents (the typed Sale Amounts), never merged.
        byMethod: Record<string, { leads: number; conversions: number; closeRate: number; revenueCents: number; avgSaleAmountCents: number; revenuePerLeadCents: number; revenuePerLeadIncomplete?: boolean; hasClosed: boolean; realSubscribers?: number; realMrrCents?: number; realCollectedCents?: number; realOneTimeCents?: number; realCollectedIncomplete?: boolean; manualRecurringCents?: number; manualMrrCents?: number; combinedRevenueCents?: number; combinedIncomplete?: boolean; suggestion?: AnalyticsSuggestion }>;
         byLeadSource?: Record<string, { leads: number; conversions: number; closeRate: number; revenueCents: number; avgSaleAmountCents: number; revenuePerLeadCents: number; revenuePerLeadIncomplete?: boolean; hasClosed: boolean; realSubscribers?: number; realMrrCents?: number; realCollectedCents?: number; realOneTimeCents?: number; realCollectedIncomplete?: boolean; manualRecurringCents?: number; manualMrrCents?: number; combinedRevenueCents?: number; combinedIncomplete?: boolean; suggestion?: AnalyticsSuggestion }>;
        byChannel: Record<string, { leads: number; conversions: number; closeRate: number; revenueCents: number; avgSaleAmountCents: number; revenuePerLeadCents: number; revenuePerLeadIncomplete?: boolean; hasClosed: boolean; realSubscribers?: number; realMrrCents?: number; realCollectedCents?: number; realOneTimeCents?: number; realCollectedIncomplete?: boolean; manualRecurringCents?: number; manualMrrCents?: number; combinedRevenueCents?: number; combinedIncomplete?: boolean; suggestion?: AnalyticsSuggestion }>;
        byCombination: Record<string, { source: string; channel: string; leads: number; conversions: number; closeRate: number; revenueCents: number; avgSaleAmountCents: number; revenuePerLeadCents: number; revenuePerLeadIncomplete?: boolean }>;
        // Legacy compatibility response retained for older clients.
        byCampaign: Record<string, CampaignBucket>;
        // Canonical first-touch Flow breakdown. Includes the six product Flows,
        // named Trackable Asset rows when attributed, legacy Offer rows, and an
        // explicit Unknown bucket when a pipeline lead has no Flow identity.
        byFlow: Record<string, CampaignBucket>;
        funnelBySource: Record<string, { stage: string; count: number; dropOff: number; dropOffRate: number }[]>;
        // Lifecycle funnel (Task 2102): generic Leads → Consult → Won counts
        // over the pipeline subset. consults uses reached-at-least semantics
        // (consult date set OR Won); consultsBooked is the literal
        // consult-date count. Optional for back-compat with cached responses.
        funnel?: {
          leads: number;
          consults: number;
          consultsBooked: number;
          won: number;
          bySource: Record<string, { leads: number; consults: number; won: number }>;
        };
        // Monthly Momentum: an independent trailing 12 calendar months (UTC,
        // oldest first) of Leads Generated (by contact-created month) and
        // collected revenue by payment month: Stripe payment dates plus manual
        // payment dates and recurring billing anniversaries. incomplete=true → some
        // Stripe lookups failed, so the revenue line is PARTIAL — the UI
        // labels it, never renders it as exact. Optional for back-compat
        // with cached responses.
        monthlyTrend?: {
          months: { month: string; leads: number; revenueCents: number }[];
          incomplete: boolean;
          // Optional attribution drills. Source and Channel use their Analytics
          // breakdown identity plus Unknown fallback. Flow contains only a
          // qualifying canonical first-touch Flow or named Trackable Asset;
          // unattributed activity remains only in the combined series. Revenue
          // adds dated Stripe and manual streams, and each series carries its
          // own partial flag when a Stripe failure can be isolated.
          bySource?: Record<
            string,
            {
              months: { month: string; leads: number; revenueCents: number }[];
              incomplete: boolean;
            }
          >;
           byLeadSource?: Record<string, {
             name?: string;
             months: { month: string; leads: number; revenueCents: number }[];
             incomplete: boolean;
           }>;
          byChannel?: Record<string, {
            months: { month: string; leads: number; revenueCents: number }[];
            incomplete: boolean;
          }>;
          byFlow?: Record<string, {
            name: string;
            months: { month: string; leads: number; revenueCents: number }[];
            incomplete: boolean;
          }>;
        };
        // Real Stripe revenue summary (Task 1919). configured:false = Stripe
        // gate off — the Subscriptions column is hidden entirely. Optional
        // for back-compat with cached responses.
        realRevenue?: {
          configured: boolean;
          matchedContacts: number;
          totalMrrCents: number;
          totalCollectedCents: number;
          // One-time (non-subscription) Stripe payments, net of refunds
          // (Task 1923). Partial when lookupFailures > 0.
          totalOneTimeCents: number;
          lookupFailures: number;
        };
        // Manual recurring revenue summary (Task 1924): user-typed assumed
        // recurring entries projected to date + as a monthly rate. Estimates
        // — separate from totalRevenueCents and realRevenue. Optional for
        // back-compat with cached responses.
        manualRecurring?: {
          contacts: number;
          totalProjectedCents: number;
          totalMrrCents: number;
          // Same figures over NON-Stripe-matched contacts only — safe to add
          // to realRevenue totals without double counting. Optional for
          // back-compat with cached responses.
          nonStripeProjectedCents?: number;
          nonStripeMrrCents?: number;
        };
        // Revenue headline: Stripe payments plus applicable manually entered
        // one-time and recurring revenue. incomplete=true → partial because
        // Stripe data is missing; known manual revenue remains included.
        combined?: {
          totalCents: number;
          incomplete: boolean;
          stripeMatched: number;
        };
        // One-time (non-recurring) sales add Stripe payments and manually
        // recorded purchases. Source fields reconcile to totalCents and remain
        // optional for back-compat with cached responses.
        oneTimeSales?: {
          totalCents: number;
          incomplete: boolean;
          stripeCents?: number;
          manualCents?: number;
          stripeIncomplete?: boolean;
        };
        // Client retention & lifetime value (Task 2101). "Client" = Won
        // contact. Lifespan uses dated Stripe subscription and one-time
        // payments plus dated manual payments, recurring boundaries, and Won
        // fallback dates; incomplete marks unavailable Stripe history. LTV
        // adds applicable Stripe collections and all manually
        // entered one-time and recurring revenue; failed Stripe retrieval
        // preserves known manual revenue and sets incomplete. avgMonths/
        // avgCents are null until 3+ clients (maturity gate → the UI renders
        // "N/A", never an average of one).
        // lostDerivable:false = no stage/outcome history table exists, so
        // clients LOST this month is not reported. Optional for back-compat
        // with cached responses.
        clientMetrics?: {
          totalClients: number;
          activeClients: number;
          gainedThisMonth: number;
          lostDerivable: boolean;
          lifespan: { avgMonths: number | null; sampleSize: number; incomplete: boolean };
          ltv: { avgCents: number | null; incomplete: boolean };
          month: {
            retainedCents: number;
            newSalesCents: number;
            newSalesStripeExcluded: boolean;
          };
        };
        // Time to Close: all-time mean whole-number days from first observed
        // pipeline entry to Won. Legacy creation-date fallbacks are explicitly
        // flagged approximate. null = fewer than 3 qualifying Won contacts.
        avgDaysToClose?: number | null;
        avgDaysToCloseApproximate?: boolean;
      }>(`/api/leads/analytics${qs ? `?${qs}` : ""}`);
    },
    // Which of the caller's contacts are Stripe-matched + their current
    // monthly Stripe MRR (mirror only, cheap). configured:false = connector
    // off → render manual estimates unchanged (Task 1933).
    stripeStatus: () =>
      fetchJSON<{ configured: boolean; mrrCentsByLead: Record<string, number>; emailMatchedLeadIds?: number[]; lifecycleMatchedLeadIds?: number[] }>(
        "/api/leads/stripe-status",
      ),
  },
  // Trackable links: short /t/<code> links with stored click/lead history.
  trackingLinks: {
    getAll: () =>
      fetchJSON<{ links: TrackingLinkRow[]; hasPublicSite: boolean }>("/api/tracking-links"),
    destinations: (scope?: "trackable-assets") =>
      fetchJSON<{ options: { value: string; label: string }[]; groups?: TrackableDestinationOption[] }>(`/api/tracking-links/destinations${scope ? `?scope=${scope}` : ""}`),
    create: (data: { name: string; destination: string }) =>
      fetchJSON<{ id: number; code: string }>("/api/tracking-links", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: number, data: { name?: string; archived?: boolean; trackingEnabled?: boolean }) =>
      fetchJSON<{ id: number }>(`/api/tracking-links/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
  },
  trackableAssets: {
    getAll: () =>
      fetchJSON<{ links: TrackingLinkRow[]; hasPublicSite: boolean }>("/api/tracking-links?scope=trackable-assets"),
    create: (data: { type: "link" | "qr"; name: string; destination?: string }) =>
      fetchJSON<{
        id: number;
        code: string;
        name: string;
        type: "link" | "qr";
        url: string;
        archived: boolean;
        trackingEnabled: boolean;
      }>("/api/trackable-assets", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: number, data: { name?: string; destination?: string; archived?: boolean; trackingEnabled?: boolean }) =>
      fetchJSON<{ id: number; name: string; destination: string; archived: boolean; trackingEnabled: boolean }>(`/api/trackable-assets/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    remove: (id: number) =>
      fetchJSON<void>(`/api/trackable-assets/${id}`, { method: "DELETE" }),
  },
  // Window QR (Task 2111): stable /t/<code> link that opens the public page
  // with Digi's guided pricing conversation (?digi=pricing). Get-or-create.
  windowQr: {
    get: () => fetchJSON<{ code: string; url: string }>("/api/window-qr"),
  },
  // QR door check-in (Task 2107): overview for the Classes page section and
  // the owner-defined loyalty settings.
  checkins: {
    overview: () => fetchJSON<CheckinOverview>("/api/checkins/overview"),
    updateSettings: (data: {
      displayName?: string;
      heading?: string;
      buttonText?: string;
      contactMessage?: string;
      thankYouTitle?: string;
      welcomeBackMessage?: string;
      targetVisits?: number;
      rewardText?: string;
      notifyOnReward?: boolean;
      notifyByEmail?: boolean;
      notifyBySms?: boolean;
      notificationEmails?: string[];
      notificationMobile?: string;
      notificationMobiles?: string[];
      emailSubject?: string;
      emailBody?: string;
      textBody?: string;
      emailOptions?: CheckinNotificationEmailOptions;
    }) =>
      fetchJSON<{
        displayName: string;
        heading: string;
        buttonText: string;
        contactMessage: string;
        thankYouTitle: string;
        welcomeBackMessage: string;
        targetVisits: number;
        rewardText: string;
        notifyOnReward: boolean;
        notifyByEmail: boolean;
        notifyBySms: boolean;
        notificationEmails: string[];
        notificationMobile: string;
        notificationMobiles: string[];
        emailSubject: string;
        emailBody: string;
        textBody: string;
        emailOptions: CheckinNotificationEmailOptions;
      }>("/api/checkins/settings", {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    // "Haven't been in lately" (Task 2147): clients drifting from their own
    // attendance pattern, plus per-client snooze.
    attention: () => fetchJSON<AttendanceAttentionResponse>("/api/checkins/attention"),
    snoozeAttention: (leadId: number, days?: number) =>
      fetchJSON<{ snoozedUntil: string }>(`/api/leads/${leadId}/attendance-snooze`, {
        method: "POST",
        body: JSON.stringify(days ? { days } : {}),
      }),
  },
  warnings: {
    get: (today?: string) => fetchJSON<{
      overdueTaskCount: number;
      leadsMissingFollowUpCount: number;
      wonMissingSaleAmountCount: number;
       leadsMissingLeadSourceCount?: number;
      pendingTestimonialCount: number;
      pendingReceptionistKbCount: number;
      staleReceptionistKbCount: number;
      subscriptionWarning: null | { status: "past_due" | "canceled" | "active" | string; cancelAt: string | null };
      captureLayerEmailWarning: null | { reason: string; at: string };
      stoppedEmailCampaigns?: import("@shared/scheduledEmailWarnings").StoppedEmailCampaignWarning[];
      emailDeliverabilityWarning?: null | {
        severity: "warning" | "critical";
        title: string;
        message: string;
        href: string;
      };
      customDomainWarning: null | { domain: string; status: string; reason: string };
      smsWarning: null | { kind: "auth" | "webhook"; reason: string; at: string | null };
      socialDisconnectedCount: number;
    }>(`/api/warnings${today ? `?today=${today}` : ""}`),
    // Clear the email-failure warning without sending anything.
    dismissEmailFailure: () =>
      fetchJSON<{ ok: boolean }>("/api/warnings/email-failure/dismiss", { method: "POST" }),
    dismissEmailDeliverability: () =>
      fetchJSON<{ ok: boolean }>("/api/warnings/email-deliverability/dismiss", { method: "POST" }),
    // Send a test email to the owner's own verified sender; clears the warning
    // on success. Throws with a friendly message when the send fails.
    testEmailFailure: () =>
      fetchJSON<{ ok: boolean; to?: string }>("/api/warnings/email-failure/test", { method: "POST" }),
  },
  goals: {
    // Live-metric goals carry a metricConfig plus server-computed progress:
    // revenue goals get currentCents (null = lookup incomplete, render "—"),
    // active-clients goals get currentCount (live, never stored).
    getAll: () => fetchJSON<(Goal & { currentCents?: number | null; currentCount?: number | null })[]>("/api/goals"),
    getTasks: (goalId: number) => fetchJSON<Task[]>(`/api/goals/${goalId}/tasks`),
    create: (data: {
      title: string;
      description?: string;
      category?: string;
      completeBy?: string | null;
      definitionOfDone?: string;
      reward?: string;
      completed?: boolean;
      showOnDashboard?: boolean;
      metricConfig?: GoalMetricConfig | null;
    }) =>
      fetchJSON<Goal>("/api/goals", { method: "POST", body: JSON.stringify(data) }),
    update: (id: number, data: Partial<Goal>) =>
      fetchJSON<Goal>(`/api/goals/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    delete: (
      id: number,
      taskAction: "keep" | "remove" = "remove",
      taskReassign?: import("./applyTaskReassign").TaskReassignWirePlan,
    ) =>
      fetchJSON<void>(`/api/goals/${id}?taskAction=${taskAction}`, {
        method: "DELETE",
        body: taskReassign ? JSON.stringify({ taskReassign }) : undefined,
      }),
    complete: (id: number, taskAction: "remove" | "keep") =>
      fetchJSON<Goal>(`/api/goals/${id}/complete`, { method: "POST", body: JSON.stringify({ taskAction }) }),
    reorder: (items: { id: number; sortOrder: number }[]) =>
      fetchJSON<{ ok: boolean }>("/api/goals/reorder", { method: "PATCH", body: JSON.stringify(items) }),
  },
  goalSteps: {
    getAll: (goalId: number) => fetchJSON<GoalStep[]>(`/api/goals/${goalId}/steps`),
    create: (goalId: number, data: { title: string; notes?: string; linkUrl?: string }) =>
      fetchJSON<GoalStep>(`/api/goals/${goalId}/steps`, { method: "POST", body: JSON.stringify(data) }),
    update: (id: number, data: Partial<GoalStep>) =>
      fetchJSON<GoalStep>(`/api/goal-steps/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    delete: (id: number) =>
      fetchJSON<void>(`/api/goal-steps/${id}`, { method: "DELETE" }),
    reorder: (goalId: number, items: { id: number; sortOrder: number }[]) =>
      fetchJSON<{ ok: boolean }>(`/api/goals/${goalId}/steps/reorder`, { method: "PATCH", body: JSON.stringify(items) }),
  },
  sources: {
    getAll: () => fetchJSON<Source[]>("/api/sources"),
    usage: () => fetchJSON<Record<string, number>>("/api/sources/usage"),
    create: (data: { name: string }) =>
      fetchJSON<Source>("/api/sources", { method: "POST", body: JSON.stringify(data) }),
    rename: (id: number, name: string) =>
      fetchJSON<Source>(`/api/sources/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
    delete: (id: number, reassignTo?: string | null) =>
      fetchJSON<void>(
        `/api/sources/${id}${reassignTo ? `?reassignTo=${encodeURIComponent(reassignTo)}` : ""}`,
        { method: "DELETE" },
      ),
    reorder: (items: { id: number; sortOrder: number }[]) =>
      fetchJSON<{ ok: boolean }>("/api/sources/reorder", { method: "PATCH", body: JSON.stringify(items) }),
  },
  directions: {
    getAll: () => fetchJSON<Direction[]>("/api/directions"),
    create: (data: { name: string }) =>
      fetchJSON<Direction>("/api/directions", { method: "POST", body: JSON.stringify(data) }),
    rename: (id: number, name: string) =>
      fetchJSON<Direction>(`/api/directions/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
    delete: (id: number) =>
      fetchJSON<void>(`/api/directions/${id}`, { method: "DELETE" }),
    reorder: (items: { id: number; sortOrder: number }[]) =>
      fetchJSON<{ ok: boolean }>("/api/directions/reorder", { method: "PATCH", body: JSON.stringify(items) }),
  },
  channels: {
    getAll: () => fetchJSON<Channel[]>("/api/channels"),
    usage: () => fetchJSON<Record<string, number>>("/api/channels/usage"),
    create: (data: { name: string }) =>
      fetchJSON<Channel>("/api/channels", { method: "POST", body: JSON.stringify(data) }),
    rename: (id: number, name: string) =>
      fetchJSON<Channel>(`/api/channels/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
    delete: (id: number, reassignTo?: string | null) =>
      fetchJSON<void>(
        `/api/channels/${id}${reassignTo ? `?reassignTo=${encodeURIComponent(reassignTo)}` : ""}`,
        { method: "DELETE" },
      ),
    reorder: (items: { id: number; sortOrder: number }[]) =>
      fetchJSON<{ ok: boolean }>("/api/channels/reorder", { method: "PATCH", body: JSON.stringify(items) }),
  },
  relationships: {
    getAll: () => fetchJSON<Relationship[]>("/api/relationships"),
    create: (data: { name: string }) =>
      fetchJSON<Relationship>("/api/relationships", { method: "POST", body: JSON.stringify(data) }),
    rename: (id: number, name: string) =>
      fetchJSON<Relationship>(`/api/relationships/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
    delete: (id: number) =>
      fetchJSON<void>(`/api/relationships/${id}`, { method: "DELETE" }),
    reorder: (items: { id: number; sortOrder: number }[]) =>
      fetchJSON<{ ok: boolean }>("/api/relationships/reorder", { method: "PATCH", body: JSON.stringify(items) }),
  },
  taskFolders: {
    getAll: () => fetchJSON<TaskFolder[]>("/api/task-folders"),
    create: (data: { name: string }) =>
      fetchJSON<TaskFolder>("/api/task-folders", { method: "POST", body: JSON.stringify(data) }),
    rename: (id: number, name: string) =>
      fetchJSON<TaskFolder>(`/api/task-folders/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
    delete: (id: number) =>
      fetchJSON<void>(`/api/task-folders/${id}`, { method: "DELETE" }),
    reorder: (items: { id: number; sortOrder: number }[]) =>
      fetchJSON<{ ok: boolean }>("/api/task-folders/reorder", { method: "PATCH", body: JSON.stringify(items) }),
  },
  classSchedule: {
    getAll: () => fetchJSON<ClassScheduleEntry[]>("/api/class-schedule"),
    // Per-date cancellations (Task 2148) + moves (Task 2167, newStartTime
    // "" = cancelled, "HH:MM" = moved to that time for the date).
    getExceptions: () =>
      fetchJSON<{ id: number; entryId: number; date: string; note: string; newStartTime: string; entryTitle: string }[]>(
        "/api/class-schedule/exceptions",
      ),
    // Batch reserved-count lookup for the Dashboard Rosters widget.
    // Returns { counts: { "id:date": number } } in one request.
    getRosterCounts: (occurrences: Array<{ entryId: number; date: string }>) => {
      if (occurrences.length === 0) return Promise.resolve({ counts: {} as Record<string, number> });
      const param = occurrences.map((o) => `${o.entryId}:${encodeURIComponent(o.date)}`).join(",");
      return fetchJSON<{ counts: Record<string, number> }>(`/api/class-schedule/roster-counts?occurrences=${param}`);
    },
    // Roster for one occurrence: who saved a spot, check-in status, spots used.
    getRoster: (id: number, date: string) =>
      fetchJSON<{
        entry: ClassScheduleEntry;
        date: string;
        capacity: number;
        roster: {
          id: number;
          name: string;
          phone: string;
          leadId: number | null;
          contactName: string;
          status: string;
          reminderStatus: string | null;
          checkedInAt: string | null;
          createdAt: string;
        }[];
      }>(`/api/class-schedule/${id}/roster?date=${encodeURIComponent(date)}`),
    // Owner-side spot add (no public RSVP flow required).
    addRsvp: (id: number, data: { date: string; leadId: number }) =>
      fetchJSON<{ ok: boolean; status: string }>(`/api/class-schedule/${id}/rsvp`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    // Owner-side removal of one reservation.
    removeRsvp: (rsvpId: number) =>
      fetchJSON<{ ok: boolean }>(`/api/class-schedule/rsvps/${rsvpId}`, { method: "DELETE" }),
    // Toggle check-in on/off for a reservation (owner-side manual override).
    toggleCheckIn: (rsvpId: number) =>
      fetchJSON<{ ok: boolean; checkedInAt: string | null }>(`/api/class-schedule/rsvps/${rsvpId}/checkin`, { method: "PATCH" }),
  },
  // Session Types (Task 2724): groups of recurring schedule slots under a
  // named type (e.g. "Morning Bootcamp" — group, or "1-on-1 Coaching" — one-on-one).
  sessionTypes: {
    getAll: () =>
      fetchJSON<SessionType[]>("/api/session-types"),
    getPrivateAppointments: () =>
      fetchJSON<PrivateOneOnOneAppointment[]>("/api/session-types/private-appointments"),
    updatePrivateAppointment: (entryId: number, data: PrivateOneOnOneAppointmentUpdateInput) =>
      fetchJSON<{ entry: ClassScheduleEntry; rsvpId: number }>(`/api/session-types/private-appointments/${entryId}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    deletePrivateAppointment: (entryId: number) =>
      fetchJSON<{ ok: boolean }>(`/api/session-types/private-appointments/${entryId}`, {
        method: "DELETE",
      }),
    create: (data: SessionTypeInput) =>
      fetchJSON<SessionType>("/api/session-types", { method: "POST", body: JSON.stringify(data) }),
    update: (id: number, data: Partial<SessionTypeInput> & { adoptEntryIds?: number[] }) =>
      fetchJSON<SessionType>(`/api/session-types/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    delete: (id: number) =>
      fetchJSON<{ ok: boolean }>(`/api/session-types/${id}`, { method: "DELETE" }),
    bookOneOnOne: (id: number, data: OneOnOneBookingInput) =>
      fetchJSON<{ entry: ClassScheduleEntry; rsvpId: number }>(`/api/session-types/${id}/book-one-on-one`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    createAndBookOneOnOne: (data: NewOneOnOneBookingInput) =>
      fetchJSON<{ sessionType: SessionType; entry: ClassScheduleEntry; rsvpId: number }>("/api/session-types/book-one-on-one", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  },
  orphanStages: {
    list: () =>
      fetchJSON<{ stage: string; count: number }[]>("/api/leads/orphan-stages"),
    resolve: (fromStage: string, toStage: string | null) =>
      fetchJSON<{ moved: number }>("/api/leads/orphan-stages/resolve", {
        method: "POST",
        body: JSON.stringify({ fromStage, toStage }),
      }),
  },
  pipelineStages: {
    getAll: () => fetchJSON<PipelineStage[]>("/api/pipeline-stages"),
    create: (data: { name: string; slug: string; subtitle?: string; sortOrder?: number }) =>
      fetchJSON<PipelineStage>("/api/pipeline-stages", { method: "POST", body: JSON.stringify(data) }),
    update: (id: number, data: { name?: string; subtitle?: string | null }) =>
      fetchJSON<PipelineStage>(`/api/pipeline-stages/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    delete: (id: number, reassignTo?: string | null) =>
      fetchJSON<void>(
        `/api/pipeline-stages/${id}${reassignTo ? `?reassignTo=${encodeURIComponent(reassignTo)}` : ""}`,
        { method: "DELETE" },
      ),
    reorder: (items: { id: number; sortOrder: number }[]) =>
      fetchJSON<{ ok: boolean }>("/api/pipeline-stages/reorder", { method: "PATCH", body: JSON.stringify(items) }),
  },
  baseline: {
    get: () => fetchJSON<BaselineResponse>("/api/baseline"),
    update: (data: BaselineUpdate) =>
      trackIntakeFactSave("baseline", () => fetchJSON<{ ok: boolean }>("/api/baseline", { method: "PATCH", body: JSON.stringify(data) })),
  },
  newsletterSync: {
    get: () => fetchJSON<NewsletterSyncConfig>("/api/newsletter-sync"),
    update: (data: NewsletterSyncUpdate) =>
      fetchJSON<NewsletterSyncConfig>("/api/newsletter-sync", { method: "PATCH", body: JSON.stringify(data) }),
    listPublications: () =>
      fetchJSON<{ publications: NewsletterPublication[] }>("/api/newsletter-sync/beehiiv/publications", { method: "POST" }),
    verify: (connector: "beehiiv" | "webhook") =>
      fetchJSON<{ ok: boolean; error?: string }>("/api/newsletter-sync/verify", { method: "POST", body: JSON.stringify({ connector }) }),
  },
  settings: {
    getEmailConfig: () => fetchJSON<SettingsEmailConfig>("/api/settings/email"),
    saveEmailSettings: (update: EmailSettingsUpdate) =>
      fetchJSON<{ ok: boolean }>("/api/settings/email", { method: "PUT", body: JSON.stringify(update) }),
    getManagedEmailDomain: () => fetchJSON<ManagedEmailDomainConfig>("/api/settings/email/domain"),
    createManagedEmailDomain: (domain: string, emailStart: string, replyTo?: string) =>
      fetchJSON<ManagedEmailDomainConfig>("/api/settings/email/domain", {
        method: "POST",
        body: JSON.stringify({ domain, emailStart, ...(replyTo ? { replyTo } : {}) }),
      }),
    verifyManagedEmailDomain: () =>
      fetchJSON<ManagedEmailDomainConfig>("/api/settings/email/domain/verify", { method: "POST", body: "{}" }),
    testManagedEmail: (data: { to: string; fromName: string; fromEmail: string; replyTo: string }) =>
      fetchJSON<{ ok: boolean }>("/api/settings/email/domain/test", { method: "POST", body: JSON.stringify(data) }),
    activateManagedEmail: () =>
      fetchJSON<{ ok: boolean; status: string }>("/api/settings/email/domain/activate", { method: "POST", body: "{}" }),
    get: () => fetchJSON<SettingsResponse>("/api/settings"),
    update: (data: Record<string, string | number | boolean | null>) =>
      fetchJSON<Record<string, unknown>>("/api/settings", {
        method: "PATCH",
        body: JSON.stringify(data),
        keepalive: Object.keys(data).every((key) => key === "showClientGoals" || key === "goalCheckInDays" || key === "showMedicalInformation"),
      }),
  },
  mergeTags: {
    get: () => fetchJSON<{ tags: MergeTagOption[] }>("/api/merge-tags"),
    update: (mergeTagValues: Record<string, { value: string; label?: string }>) =>
      fetchJSON<{ tags: MergeTagOption[] }>("/api/merge-tags", { method: "PATCH", body: JSON.stringify({ mergeTagValues }) }),
    create: (data: { label: string; value: string }) =>
      fetchJSON<{ tags: MergeTagOption[] }>("/api/merge-tags", { method: "POST", body: JSON.stringify(data) }),
    remove: (key: string) =>
      fetchJSON<{ tags: MergeTagOption[] }>(`/api/merge-tags/${encodeURIComponent(key)}`, { method: "DELETE" }),
  },
  emailPreferences: {
    resubscribeLead: (leadId: number) =>
      fetchJSON<Lead>(`/api/leads/${leadId}/email-preferences/resubscribe`, {
        method: "POST",
        body: JSON.stringify({ explicitContactRequest: true }),
      }),
  },
  messaging: {
    // Sent-history for the Messaging page's stats tab: modern email rows are
    // grouped by their send operation (legacy rows use a conservative time
    // fallback), email batches.
    history: (campaign?: string) =>
      fetchJSON<{
        emailBatches: {
          sentAt: string;
          subject: string;
          fromName: string;
          // Attribution (Task 1950): member who sent the batch (null = owner).
          sentByUserId: string | null;
          recipients: number;
          // Delivery tracking, polled from the user's own Resend account.
          opened: number;
          bounced: number;
           suppressed: number;
          // Spam complaints, separate from unsubscribes.
          complained: number;
          unsubscribed: number;
          // Per-recipient delivery detail. `event` is Resend's last_event
          // verbatim ("" = no activity yet), or "unsubscribed" from our own
          // link; tracked=false for rows sent before tracking existed.
          people: { leadId: number; event: string; tracked: boolean }[];
           peopleTruncated: boolean;
           batchKey?: string | null;
           scheduledRecipients?: number;
           sentRecipients?: number;
           failedRecipients?: number;
           skippedRecipients?: number;
           status?: string;
           statusDetail?: string;
           diagnostic?: EmailDiagnostic | null;
           lifecycle?: EmailLifecycleTransition[];
           skippedPeople?: { leadId: number; reason: string }[];
           skippedPeopleTruncated?: boolean;
        }[];
        deliveryRefresh: {
          id: string;
          status: "running" | "complete" | "failed";
        } | null;
      }>(`/api/messaging/history${campaign ? `?campaign=${encodeURIComponent(campaign)}` : ""}`),
    deliveryRefreshStatus: (refreshId: string) =>
      fetchJSON<{
        id: string;
        status: "running" | "complete" | "failed" | "unknown";
      }>(`/api/messaging/history/refresh-status?refreshId=${encodeURIComponent(refreshId)}`),
    reviewPausedEmails: (batchId: number) => fetchJSON<{
      rows: Array<{
        recipientId: number; name: string; email: string; status: string;
        outcome: "accepted" | "not_accepted" | "uncertain"; explanation: string;
      }>;
    }>(`/api/email/scheduled/${batchId}/recovery`),
    recoverPausedEmail: (batchId: number, recipientId: number, action: "record_acceptance" | "retry") =>
      fetchJSON<{ ok: boolean }>(`/api/email/scheduled/${batchId}/recovery`, {
        method: "POST", body: JSON.stringify({ recipientId, action, confirmed: true }),
      }),
    campaignRecipients: (
      batchKey: string,
      options: { page?: number; limit?: number; search?: string; status?: string } = {},
    ) => {
      const params = new URLSearchParams({
        batchKey,
        page: String(options.page || 1),
        limit: String(options.limit || 50),
      });
      if (options.search) params.set("search", options.search);
      if (options.status && options.status !== "all") params.set("status", options.status);
      return fetchJSON<{
        rows: { key: string; leadId: number; name: string; email: string; status: string; contactExists: boolean; diagnostic?: EmailDiagnostic | null }[];
        total: number;
        statuses: string[];
        page: number;
        limit: number;
      }>(`/api/messaging/history/recipients?${params.toString()}`);
    },
    legacyHistoryContacts: (leadIds: number[]) =>
      fetchJSON<Array<Pick<Lead, "id" | "firstName" | "lastName" | "handle" | "email">>>(
        "/api/messaging/history/legacy-contacts",
        {
          method: "POST",
          body: JSON.stringify({ leadIds }),
        },
      ),
    campaignRecipientsCsv: (
      batchKey: string,
      options: { search?: string; status?: string } = {},
    ) => {
      const params = new URLSearchParams({ batchKey });
      if (options.search) params.set("search", options.search);
      if (options.status && options.status !== "all") params.set("status", options.status);
      return downloadBlob(`/api/messaging/history/recipients.csv?${params.toString()}`);
    },
    emailMetrics: (startDate: string, endDate: string) =>
      fetchJSON<EmailMetricsResponse>(
        `/api/messaging/email-metrics?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
      ),
  },
  // ── BYO Stripe (Task 1917/1946): the user's OWN connected Stripe account ─
  // Connected by installing the InControl Stripe App (read-only permissions
  // enforced by Stripe): billing history + real revenue for THEIR clients,
  // never charging/refunding/invoicing.
  stripeConnect: {
    status: () => fetchJSON<StripeConnectStatus>("/api/stripe-connect"),
    // Returns the Stripe authorize URL; the caller navigates the browser there.
    start: () =>
      fetchJSON<{ url: string }>("/api/stripe-connect/start", { method: "POST" }),
    disconnect: () =>
      fetchJSON<{ ok: boolean }>("/api/stripe-connect", { method: "DELETE" }),
    searchCustomers: (query: string) =>
      fetchJSON<{ customers: StripeConnectCustomer[] }>(
        `/api/stripe-connect/customers?query=${encodeURIComponent(query)}`,
      ),
    // Per-contact match/link facts + the per-user billing history variant
    // (same shape as the admin one so the list UI is shared).
    getContactLink: (leadId: number) =>
      fetchJSON<ContactStripeLink>(`/api/contact-stripe/${leadId}`),
    linkContact: (leadId: number, customerId: string) =>
      fetchJSON<{ ok: boolean; customerId: string }>(`/api/contact-stripe/${leadId}/link`, {
        method: "POST",
        body: JSON.stringify({ customerId }),
      }),
    unlinkContact: (leadId: number) =>
      fetchJSON<{ ok: boolean }>(`/api/contact-stripe/${leadId}/link`, { method: "DELETE" }),
    getContactBillingHistory: (leadId: number) =>
      fetchJSON<ContactBillingHistory>(`/api/contact-stripe/${leadId}/billing-history`),
    getContactSubscriptions: (leadId: number) =>
      fetchJSON<ContactClientSubscriptions>(`/api/contact-stripe/${leadId}/subscriptions`),
    getContactRevenue: (leadId: number) =>
      fetchJSON<ContactRealRevenueFigures>(`/api/contact-stripe/${leadId}/revenue`),
    // Account-wide match review (Task 1930): matched contacts (with match
    // source) + Stripe customers no contact matched. Server 502s on lookup
    // failure — never a silently empty list.
    matches: () => fetchJSON<StripeConnectMatches>("/api/stripe-connect/matches"),
    // Bulk "create contacts for all" (Task 1938): server recomputes the
    // unmatched set, creates + manually links a contact per customer, skips
    // failures per-customer, and reports an explicit summary. Task 1940: an
    // optional allowlist limits the run to the checked rows — the server
    // intersects it with its own recomputed unmatched set.
    createContactsForUnmatched: (customerIds?: string[]) =>
      fetchJSON<StripeConnectBulkCreateResult>(
        "/api/stripe-connect/matches/create-contacts",
        {
          method: "POST",
          ...(customerIds ? { body: JSON.stringify({ customerIds }) } : {}),
        },
      ),
  },
  billing: {
    // Authoritative status (live Stripe lookup + mirror refresh).
    status: () =>
      fetchJSON<{
        configured: boolean;
        active: boolean;
        status: string | null;
        cancelAt: string | null;
        hasCustomer: boolean;
      }>("/api/billing/status"),
    // Returns the hosted Stripe Checkout URL to redirect to for the chosen
    // billing interval (monthly by default). A valid promo code switches the
    // checkout to that code's private rate.
    // `returnTo` is a server-validated NAME (never a URL) that picks where
    // Stripe sends the buyer back: "subscribe" (default) or "onboarding"
    // (mid-onboarding payment stop, Task 2249).
    checkout: (
      interval: "month" | "year" = "month",
      promoCode?: string,
      returnTo?: "subscribe" | "onboarding",
    ) =>
      fetchJSON<{ url: string }>("/api/billing/checkout", {
        method: "POST",
        body: JSON.stringify({
          interval,
          ...(promoCode ? { promoCode } : {}),
          ...(returnTo ? { returnTo } : {}),
        }),
      }),
    // Validate a promo code; returns the private rates it unlocks (cents).
    checkPromoCode: (code: string) =>
      fetchJSON<{
        valid: boolean;
        monthlyAmount: number | null;
        yearlyAmount: number | null;
        currency: string | null;
      }>("/api/billing/promo-code", {
        method: "POST",
        body: JSON.stringify({ code }),
      }),
    // Public live launch pricing (monthly + optional yearly) for display.
    publicPrice: () =>
      fetchJSON<{
        configured: boolean;
        amount: number | null;
        currency: string | null;
        interval: string | null;
        intervalCount: number | null;
        yearly: { amount: number | null; currency: string | null } | null;
        // True when at least one promo code is live; the Subscribe page only
        // shows its promo-code box when this is set.
        promoCodesActive?: boolean;
      }>("/api/billing/public-price"),
    // Returns the Stripe billing-portal URL (manage / cancel).
    portal: () =>
      fetchJSON<{ url: string }>("/api/billing/portal", { method: "POST" }),
  },
  workspaceMembers: {
    // Authorized users (Task 1945): owner-only management of logins that get
    // full access to this workspace. status: "invited" until first sign-in.
    list: () =>
      fetchJSON<{ id: number; email: string; status: "invited" | "active"; createdAt: string }[]>(
        "/api/workspace-members",
      ),
    add: (email: string) =>
      fetchJSON<{ id: number; email: string; status: "invited" | "active"; createdAt: string }>(
        "/api/workspace-members",
        { method: "POST", body: JSON.stringify({ email }) },
      ),
    remove: (id: number) =>
      fetchJSON<{ ok: boolean }>(`/api/workspace-members/${id}`, { method: "DELETE" }),
    // Attribution lookup (Task 1950): userId → email for everyone who has
    // ever acted in this workspace (revoked included). Readable by members.
    actors: () =>
      fetchJSON<{ actors: Record<string, string> }>("/api/workspace-actors"),
  },
  support: {
    // Send a message to the platform owner. Sender identity is resolved
    // server-side from the Clerk session. Optional screenshot/video
    // attachments travel as base64 (max 3 files, ~30MB total — the delivery
    // ceiling under Resend's 40MB encoded email limit).
    contact: (data: {
      subject: string;
      message: string;
      attachments?: { filename: string; contentType: string; content: string }[];
    }) =>
      fetchJSON<{ ok: boolean }>("/api/support/contact", { method: "POST", body: JSON.stringify(data) }),
  },
  email: {
    sendTestEmail: (data: { fromName: string; subject: string; body: string; signatureText?: string; signatureHtml?: string; attachments?: { filename: string; content: string }[]; includeHeader?: boolean; headerImageUrl?: string; headerAlign?: "left" | "center" | "right"; signatureAlign?: "left" | "center" | "right"; idempotencyKey?: string }) =>
      fetchJSON<{ ok: boolean; sentTo: string }>("/api/email/send-test", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    sendBulk: (data: { contactIds: number[]; fromName: string; subject: string; body: string; signatureText?: string; signatureHtml?: string; attachments?: { filename: string; content: string }[]; includeHeader?: boolean; headerImageUrl?: string; headerAlign?: "left" | "center" | "right"; signatureAlign?: "left" | "center" | "right"; noShortenUrls?: string[]; referralAsk?: boolean; idempotencyKey?: string; acknowledgement?: { accepted: true; termsVersion: string; operationKey: string } }) =>
      fetchJSON<BulkEmailResponse>("/api/email/send-bulk", { method: "POST", body: JSON.stringify(data) }),
    getHistory: (contactId: number) =>
      fetchJSON<EmailHistory[]>(`/api/leads/${contactId}/email-history`),
    scheduled: {
      list: () => fetchJSON<ScheduledEmail[]>("/api/email/scheduled"),
      create: (data: { scheduledAt: string; timezone: string; contactIds: number[]; fromName: string; subject: string; body: string; signatureText?: string; signatureHtml?: string; attachments?: { filename: string; content: string }[]; headerImageUrl?: string; headerAlign?: "left" | "center" | "right"; signatureAlign?: "left" | "center" | "right"; noShortenUrls?: string[]; acknowledgement?: { accepted: true; termsVersion: string; operationKey: string } }) =>
        fetchJSON<ScheduledEmail>("/api/email/scheduled", { method: "POST", body: JSON.stringify(data) }),
      detail: (id: number) => fetchJSON<ScheduledEmailDetail>(`/api/email/scheduled/${id}`),
      reschedule: (id: number, data: { scheduledAt: string; timezone: string; subject?: string; body?: string; fromName?: string; signatureText?: string; signatureHtml?: string; attachments?: { filename: string; content: string }[]; headerImageUrl?: string; headerAlign?: "left" | "center" | "right"; signatureAlign?: "left" | "center" | "right"; noShortenUrls?: string[] }) =>
        fetchJSON<ScheduledEmail>(`/api/email/scheduled/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
      cancel: (id: number) => fetchJSON<{ ok: boolean }>(`/api/email/scheduled/${id}/cancel`, { method: "POST" }),
      delete: (id: number) => fetchJSON<{ ok: boolean }>(`/api/email/scheduled/${id}`, { method: "DELETE" }),
      retry: (id: number) => fetchJSON<{ ok: boolean }>(`/api/email/scheduled/${id}/retry`, { method: "POST" }),
    },
  },
  // Resolved outreach masters (admin defaults + overrides): text-message
  // versions of the referral/review asks for the Messaging page tiles.
  outreach: {
    masters: () =>
      fetchJSON<OutreachMasterSummary[]>("/api/outreach/masters"),
  },
  config: {
    reset: () => fetchJSON<{ ok: boolean }>("/api/config/reset", { method: "POST" }),
  },
  admin: {
    // Dev-era stray accounts (Task 1861): profile owners unknown to the active
    // Clerk instance, persisted by the deployment boot scan. Visibility only.
    getDevEraStrays: () => fetchJSON<{ scan: DevEraStrayScan | null }>("/api/admin/dev-era-strays"),
    scanDevEraStrays: () =>
      fetchJSON<{ scan: DevEraStrayScan }>("/api/admin/dev-era-strays/scan", { method: "POST" }),
    resetOnboarding: () => fetchJSON<{ ok: boolean }>("/api/admin/reset-onboarding", { method: "POST" }),
    // Dev-only demo studio reseed (Task 2169). `available` is false in
    // production deployments, which hides the admin-page card entirely.
    // Task 2172: the reseed runs as a background job — the POST returns 202
    // immediately and the card polls `status` for running/success/failure.
    getDemoStudio: () =>
      fetchJSON<{ available: boolean; demoEmail: string; status: DemoReseedStatus }>("/api/admin/demo-studio"),
    resetDemoStudio: () =>
      fetchJSON<{ ok: boolean; started: boolean; runId: number }>(
        "/api/admin/demo-studio/reset",
        { method: "POST", body: JSON.stringify({ confirm: "RESET DEMO STUDIO" }) },
      ),
    // Full account reset: wipes ALL of a user's data (contacts, tasks,
    // campaigns, website, media, avatars) back to a brand-new account; keeps
    // the login + billing identity. No target = the calling admin's OWN
    // account; pass a userId to reset another (e.g. live test) account.
    // Server requires the exact confirmation phrase either way.
    resetAccount: (targetUserId?: string) =>
      fetchJSON<{ ok: boolean; userId: string }>("/api/admin/reset-account", {
        method: "POST",
        body: JSON.stringify({ confirm: "RESET MY ACCOUNT", ...(targetUserId ? { userId: targetUserId } : {}) }),
      }),
    // ── Admin "View as User" support sessions (Task 2057). Start swaps the
    // admin's effective identity to the target on every subsequent /api
    // request; end returns to the admin's own account. Every start/stop is
    // recorded in the append-only audit log. ──
    startSupportSession: (targetUserId: string) =>
      fetchJSON<{ ok: boolean }>("/api/admin/support-session", {
        method: "POST",
        body: JSON.stringify({ targetUserId }),
      }),
    endSupportSession: () =>
      fetchJSON<{ ok: boolean; ended: number }>("/api/admin/support-session", { method: "DELETE" }),
    getSupportSessions: () =>
      fetchJSON<{
        id: number;
        adminUserId: string;
        adminEmail: string | null;
        targetUserId: string;
        targetEmail: string | null;
        startedAt: string;
        endedAt: string | null;
      }[]>("/api/admin/support-sessions"),
    // ── Offer Templates (admin-authored, activated as user campaign copies) ──
    // ── Reactivation masters (Task 1819): Lynn edits the six masters once;
    // future personalizations inherit (per-field overrides over defaults). ──
    listReactivationMasters: () => fetchJSON<AdminReactivationMaster[]>("/api/admin/reactivation-masters"),
    updateReactivationMaster: (key: ReactivationGoalKey, data: Partial<Record<ReactivationField, string>>) =>
      fetchJSON<{ ok: boolean }>(`/api/admin/reactivation-masters/${key}`, { method: "PATCH", body: JSON.stringify(data) }),
    // ── Outreach masters: Request Referrals / Request Reviews email + SMS
    // (Admin → Playbooks → Customer Growth). Same per-field override model. ──
    listOutreachMasters: () => fetchJSON<AdminOutreachMaster[]>("/api/admin/outreach-masters"),
    updateOutreachMaster: (key: OutreachMasterKey, data: Partial<Record<OutreachField, string>>) =>
      fetchJSON<{ ok: boolean }>(`/api/admin/outreach-masters/${key}`, { method: "PATCH", body: JSON.stringify(data) }),
    listOfferTemplates: () => fetchJSON<OfferTemplate[]>("/api/admin/offer-templates"),
    updateOfferTemplate: (id: number, input: Partial<OfferTemplateInput>) =>
      fetchJSON<OfferTemplate>(`/api/admin/offer-templates/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    updateAccountMetadata: (userId: string, patch: { nickname?: string; adminManagedDemo?: boolean }) =>
      fetchJSON<{ userId: string; nickname: string; adminManagedDemo: boolean }>(
        `/api/admin/users/${encodeURIComponent(userId)}/account`,
        { method: "PATCH", body: JSON.stringify(patch) },
      ),
    getUsers: () => fetchJSON<{
      userId: string;
      nickname?: string;
      adminManagedDemo?: boolean;
      email: string | null;
      adminContactLeadId: number | null;
      firstName: string | null;
      lastName: string | null;
      createdAt: string | null;
      lastSignInAt: string | null;
      banned: boolean;
      isAdmin: boolean;
      // Internal account (admin or METRICS_EXCLUDED_ACCOUNT_IDS): row stays
      // in the table but is skipped from the KPI tile roll-ups.
      metricsExcluded?: boolean;
      contactCount: number;
      // Public website slug (/p/<slug>) — null when the user has no profile yet.
      publicSlug?: string | null;
      purchaseReason?: string | null;
      profitMonthlyCents?: number;
      costs?: {
        storageBytes: number;
        monthly: {
          video: number;
          avatar: number;
          voice: number;
          aiText: number;
          image: number;
          email?: number;
          storage: number;
          domain?: number;
          total: number;
        };
        lifetime: {
          video: number;
          avatar: number;
          voice: number;
          aiText: number;
          image: number;
          email?: number;
          storage: number;
          domain?: number;
          total: number;
        };
      };
      baseline: {
        leadsPerMonth: number | null;
        clientValueUsd: number | null;
        salesPerMonth: number | null;
        websiteUrl: string | null;
        submittedAt: string | null;
      } | null;
      incontrolLift: {
        widgetLeadsTotal: number;
        widgetWonTotal: number;
        accountAgeMonths: number | null;
        widgetLeadsPerMonth: number;
        widgetWonPerMonth: number;
        liftAllUsdPerMonth: number;
        liftWonUsdPerMonth: number;
        totalLeads: number;
        totalWon: number;
        wonCountLast30d: number;
        wonDollars: number;
        measuredLeadsPerMonth: number;
        measuredSalesPerMonth: number;
        measuredDollarsPerMonth: number;
        subscriptionPriceUsd: number;
        leadsMultiplier: number | null;
        salesMultiplier: number | null;
        payingForItself: {
          wonCountLast30d: number;
          wonDollarsLast30d: number;
          wonMissingAmountCountLast30d: number;
          baselineSalesPerMonth: number;
          clientValueUsd: number;
          subscriptionPriceUsd: number;
          incrementalCount: number;
          incrementalDollars: number;
          threshold: number;
          margin: number;
          isPaying: boolean;
        } | null;
      };
    }[]>("/api/admin/users"),
    getManagedEmailSummary: (workspaceId: string) =>
      fetchJSON<ManagedEmailOperationalSummary>(
        `/api/admin/workspaces/${encodeURIComponent(workspaceId)}/managed-email`,
      ),
    getEmailTransportStatus: () =>
      fetchJSON<EmailTransportStatus>("/api/admin/email-transports"),
    suspendManagedEmail: (workspaceId: string, reason: string) =>
      fetchJSON<{ ok: boolean; suspended: boolean }>(
        `/api/admin/workspaces/${encodeURIComponent(workspaceId)}/managed-email/suspend`,
        { method: "POST", body: JSON.stringify({ reason }) },
      ),
    resumeManagedEmail: (workspaceId: string) =>
      fetchJSON<{ ok: boolean; suspended: boolean }>(
        `/api/admin/workspaces/${encodeURIComponent(workspaceId)}/managed-email/resume`,
        { method: "POST" },
      ),
    // Platform-attributed spend (starter-avatar build scripts etc.) — the
    // reserved "__platform__" usage bucket, reported separately from per-user
    // rows. Cents; monthly = last 30 days. Same shape as a user's cost
    // windows so the dashboard can reuse its formatting helpers.
    // Avatar likeness-consent lookup (Task 1999): read-only per-person
    // ("HeyGen group") attestation existence + date for any user's custom
    // avatars, plus attestation records whose avatar no longer exists
    // (they survive deletion on purpose — part of the legal record).
    getUserAvatarConsent: (userId: string) => fetchJSON<{
      people: {
        heygenGroupId: string | null;
        name: string;
        lookCount: number;
        attestedAt: string | null;
        wording: string | null;
        // Snapshot of the source photo the owner attested about (null when
        // no attestation exists or none was snapshotted).
        sourceImageUrl: string | null;
      }[];
      orphanedAttestations: {
        id: number;
        heygenGroupId: string | null;
        avatarName: string;
        buildKind: string;
        attestedAt: string;
        wording: string;
        sourceImageUrl: string | null;
      }[];
    }>(`/api/admin/users/${encodeURIComponent(userId)}/avatar-consent`),
    getPlatformCosts: () => fetchJSON<{
      monthly: { video: number; avatar: number; voice: number; aiText: number; image: number; storage: number; domain: number; total: number };
      lifetime: { video: number; avatar: number; voice: number; aiText: number; image: number; storage: number; domain: number; total: number };
    }>("/api/admin/platform-costs"),
    // Static pricing/limits reference for the admin Costs & Pricing tab —
    // fixed subscriptions, at-a-glance rate card, full metered rates, and
    // per-user limits. All derived server-side from costRates.ts + shared
    // constants. Cents where numeric; display strings are pre-formatted.
    getPricing: () => fetchJSON<{
      subscriptions: { service: string; monthlyCents: number; engine: string; note: string }[];
      subscriptionTotalCents: number;
      rateCard: { item: string; costLabel: string; note: string }[];
      meteredRates: { item: string; costLabel: string; unit: string; engine: string; note: string }[];
      limits: { item: string; limitLabel: string; note: string }[];
      onboarding: {
        assumption: string;
        lines: { item: string; costLabel: string; engine: string; note: string }[];
        totalMinCents: number;
        totalMaxCents: number;
        followUpNote: string;
      };
      // Forward-looking Profit-per-User model (Task #1477): monthly unit
      // economics at each subscription price point, computed server-side.
      profitModel: {
        tables: {
          label: string;
          priceCents: number;
          rows: {
            scenario: string;
            note: string;
            revenueCents: number;
            stripeFeeCents: number;
            videoCents: number;
            overheadCents: number;
            profitCents: number;
          }[];
        }[];
        assumptions: string[];
      };
    }>("/api/admin/pricing"),
    // ── Subscriber payment status (Task #1543) ── read-only Stripe MIRROR
    // facts (user_settings columns), never a live Stripe call per row.
    // Subscription facts for one owner-CRM contact (ContactCard panel).
    getContactSubscription: (leadId: number) =>
      fetchJSON<ContactSubscription>(`/api/admin/contact-subscription/${leadId}`),
    // Billing history (Task 1916) — LIVE Stripe invoice list for a subscriber
    // contact. Read-only; configured:false = Stripe connector not connected.
    getContactBillingHistory: (leadId: number) =>
      fetchJSON<ContactBillingHistory>(`/api/admin/contact-billing-history/${leadId}`),
    // Billing roll-up for the admin Billing tab (cents end-to-end).
    getBillingReport: () => fetchJSON<BillingReport>("/api/admin/billing-report"),
    // Real collected revenue from Stripe invoices (Task 1918). Live Stripe
    // aggregation cached 10 min server-side. configured:false when the Stripe
    // connector is not wired up.
    getRevenueReport: () => fetchJSON<AdminRevenueReport>("/api/admin/revenue-report"),
    invalidateRevenueReport: () =>
      fetchJSON<{ ok: boolean }>("/api/admin/revenue-report/invalidate", { method: "POST" }),
    getBackupManifest: () => fetchJSON<{
      at: string;
      key: string;
      encBytes: number;
      dumpBytes: number;
      verified: boolean;
      mediaObjects: number;
      mediaBytes: number;
      mediaChecked?: number;
      mediaVerifyFailed?: number;
    }[]>("/api/admin/backup-manifest"),
    getCleanupHealth: () => fetchJSON<
      | {
          available: true;
          jobs: {
            name: string;
            history: { durationMs: number; slowThresholdMs: number; recordedAt: number }[];
            alertActive: boolean;
            lastAlertAt: number | null;
            lastRecoveryAt: number | null;
          }[];
        }
      | { available: false; error: string }
    >("/api/admin/cleanup-health"),
    runCoverageSmokeTest: () =>
      fetchJSON<
        { sent: true; to: string } | { sent: false; reason: string }
      >("/api/admin/backup-coverage-smoke-test", { method: "POST" }),
  },
  captureLayer: {
    getWidget: () =>
      fetchJSON<{
        widget: CaptureLayerWidget;
        pipelineStages: { slug: string; name: string }[];
        taxonomy: {
          sources: string[];
          directions: string[];
          channels: string[];
          contactTypes: string[];
        };
      }>("/api/capture-layer/widget"),
    updateWidget: (input: Partial<CaptureLayerWidgetUpdate>) =>
      fetchJSON<CaptureLayerWidget>("/api/capture-layer/widget", {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    listChips: () => fetchJSON<CaptureLayerChip[]>("/api/capture-layer/chips"),
    createChip: (input: CaptureLayerChipInput) =>
      fetchJSON<CaptureLayerChip>("/api/capture-layer/chips", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    updateChip: (id: number, input: Partial<CaptureLayerChipInput>) =>
      fetchJSON<CaptureLayerChip>(`/api/capture-layer/chips/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    deleteChip: (id: number) =>
      fetchJSON<{ success: boolean }>(`/api/capture-layer/chips/${id}`, { method: "DELETE" }),
    reorderChips: (orderedIds: number[]) =>
      fetchJSON<{ success: boolean }>("/api/capture-layer/chips/reorder", {
        method: "POST",
        body: JSON.stringify({ orderedIds }),
      }),
    // ── Multi-campaign ──
    listCampaigns: () =>
      fetchJSON<{
        publicSlug: string;
        campaigns: CaptureLayerWidget[];
        pipelineStages: { slug: string; name: string }[];
        taxonomy: {
          sources: string[];
          directions: string[];
          channels: string[];
          contactTypes: string[];
        };
      }>("/api/capture-layer/campaigns"),
    createCampaign: (input?: { name?: string; campaignType?: "pricing"; createSlide?: boolean; purpose?: "offer" | "lead-form" }) =>
      fetchJSON<CaptureLayerWidget>("/api/capture-layer/campaigns", {
        method: "POST",
        body: JSON.stringify(input ?? {}),
      }),
    updateCampaign: (id: number, input: Partial<CaptureLayerWidgetUpdate>) =>
      fetchJSON<CaptureLayerWidget>(`/api/capture-layer/campaigns/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    // AI-draft the per-campaign follow-up email (subject + body) sent to the
    // submitter, from the campaign's own details. The caller persists the result
    // onto the campaign via updateCampaign. `includeSignature` mirrors the
    // "Include My Email Signature" toggle's LIVE state (autosave may not have
    // persisted it yet) — when true the draft omits its own sign-off because
    // the owner's saved signature is appended at send time.
    draftFollowUpEmail: (
      id: number,
      includeSignature?: boolean,
      previous?: { subject: string; body: string },
    ) =>
      fetchJSON<{ subject: string; body: string }>(
        `/api/capture-layer/campaigns/${id}/draft-follow-up-email`,
        {
          method: "POST",
          body: JSON.stringify({
            ...(includeSignature === undefined ? {} : { includeSignature }),
            ...(previous ? { previous } : {}),
          }),
        },
      ),
    // Turn a campaign's Our Specials slide On/Off. Campaigns toggle
    // independently — any number can be On at once (including zero).
    activateCampaign: (id: number, active: boolean) =>
      fetchJSON<CaptureLayerWidget>(`/api/capture-layer/campaigns/${id}/activate`, {
        method: "POST",
        body: JSON.stringify({ active }),
      }),
    deleteCampaign: (id: number) =>
      fetchJSON<{ success: boolean }>(`/api/capture-layer/campaigns/${id}`, { method: "DELETE" }),
    removeSlideForm: (id: number, expectedSlideIds: number[]) =>
      fetchJSON<{ success: boolean }>(`/api/capture-layer/campaigns/${id}/slide-form`, {
        method: "DELETE",
        body: JSON.stringify({ expectedSlideIds }),
      }),
    reorderCampaigns: (orderedIds: number[]) =>
      fetchJSON<{ success: boolean }>("/api/capture-layer/campaigns/reorder", {
        method: "POST",
        body: JSON.stringify({ orderedIds }),
      }),
    listCampaignChips: (campaignId: number) =>
      fetchJSON<CaptureLayerChip[]>(`/api/capture-layer/campaigns/${campaignId}/chips`),
    createCampaignChip: (campaignId: number, input: CaptureLayerChipInput) =>
      fetchJSON<CaptureLayerChip>(`/api/capture-layer/campaigns/${campaignId}/chips`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    updateCampaignChip: (campaignId: number, id: number, input: Partial<CaptureLayerChipInput>) =>
      fetchJSON<CaptureLayerChip>(`/api/capture-layer/campaigns/${campaignId}/chips/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    deleteCampaignChip: (campaignId: number, id: number) =>
      fetchJSON<{ success: boolean }>(`/api/capture-layer/campaigns/${campaignId}/chips/${id}`, { method: "DELETE" }),
    reorderCampaignChips: (campaignId: number, orderedIds: number[]) =>
      fetchJSON<{ success: boolean }>(`/api/capture-layer/campaigns/${campaignId}/chips/reorder`, {
        method: "POST",
        body: JSON.stringify({ orderedIds }),
      }),
    // ── Branded campaign graphics (Task 1792, both designs Task 1803) ─────
    // GET returns all design rows (empty until the first render — playbook
    // installs create BOTH designs automatically when the playbook carries
    // graphics). PUT saves ONE design's inputs and re-renders its PNG
    // server-side; each design is customized independently.
    getGraphics: (campaignId: number) =>
      fetchJSON<{ graphics: CampaignGraphic[] }>(
        `/api/capture-layer/campaigns/${campaignId}/graphic`,
      ),
    saveGraphic: (campaignId: number, input: CampaignGraphicSaveInput) =>
      fetchJSON<{ graphic: CampaignGraphic }>(
        `/api/capture-layer/campaigns/${campaignId}/graphic`,
        { method: "PUT", body: JSON.stringify(input) },
      ),
    downloadGraphic: (campaignId: number, templateKey?: string) =>
      downloadBlob(
        `/api/capture-layer/campaigns/${campaignId}/graphic/download${templateKey ? `?template=${encodeURIComponent(templateKey)}` : ""}`,
      ),
    // All-time per-campaign stats for the Vault campaign cards. Keyed
    // `c:<widgetId>` (Direct row omitted — every Vault campaign is a widget).
    campaignStats: () =>
      fetchJSON<{ byCampaign: Record<string, CampaignBucket> }>(
        "/api/capture-layer/campaign-stats",
      ),
  },
  // Playbook Library: browse Lynn's shared playbooks by category and install
  // one — the server AI-personalizes the copy/scripts, creates an UNPUBLISHED
  // draft campaign, and starts its presenter renders (poll aiVideo for those).
  playbooks: {
    library: () => fetchJSON<PlaybookLibraryResponse>("/api/playbook-library"),
    install: (id: number) =>
      fetchJSON<PlaybookInstallResult>(`/api/playbooks/${id}/install`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
  },
  businessProfile: {
    get: () => fetchJSON<BusinessProfileResponse>("/api/business-profile"),
    update: (data: Partial<BusinessProfileResponse>) =>
      trackIntakeFactSave("business-profile", () => fetchJSON<BusinessProfileResponse>("/api/business-profile", {
        method: "PATCH",
        body: JSON.stringify(data),
      })),
    // Booking → CRM webhook (Task 1744): generate/rotate the secret webhook
    // URL the owner pastes into their scheduler, or disconnect it. Both return
    // the fresh profile payload (bookingWebhookToken updated).
    generateBookingWebhook: () =>
      fetchJSON<BusinessProfileResponse>("/api/booking-webhook/generate", { method: "POST" }),
    disconnectBookingWebhook: () =>
      fetchJSON<BusinessProfileResponse>("/api/booking-webhook", { method: "DELETE" }),
    // AI-draft the account-level Contact-form follow-up email (subject + body)
    // sent to the submitter. Campaign-less twin of
    // captureLayer.draftFollowUpEmail; the caller persists the result onto
    // business_profile.contactFollowUpEmail* via update().
    draftFollowUpEmail: (previous?: { subject: string; body: string }) =>
      fetchJSON<{ subject: string; body: string }>(
        "/api/business-profile/draft-follow-up-email",
        { method: "POST", body: JSON.stringify(previous ? { previous } : {}) },
      ),
    // AI-draft the testimonial thank-you follow-up email (subject + body) sent to
    // the person who just submitted a testimonial. Testimonial-tuned twin of
    // draftFollowUpEmail; the caller persists the result onto
    // business_profile.testimonialFollowUpEmail* via update().
    draftTestimonialFollowUpEmail: (previous?: { subject: string; body: string }) =>
      fetchJSON<{ subject: string; body: string }>(
        "/api/business-profile/draft-testimonial-follow-up-email",
        { method: "POST", body: JSON.stringify(previous ? { previous } : {}) },
      ),
    // AI "Clean Up" for the reusable business description. Returns THREE
    // rewrite options; the caller shows a picker and autosaves the chosen one.
    // `previous` = options already rejected (Try Again asks for different takes).
    cleanupDescription: (data: { text: string; previous?: string[] }) =>
      fetchJSON<{ text: string; options: string[] }>("/api/business-profile/describe/cleanup", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    listFaqs: () => fetchJSON<BusinessFaq[]>("/api/business-faqs"),
    createFaq: (data?: Partial<BusinessFaqInput>) =>
      fetchJSON<BusinessFaq>("/api/business-faqs", {
        method: "POST",
        body: JSON.stringify(data ?? {}),
      }),
    updateFaq: (id: number, data: Partial<BusinessFaqInput>) =>
      fetchJSON<BusinessFaq>(`/api/business-faqs/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    deleteFaq: (id: number) =>
      fetchJSON<{ success: boolean }>(`/api/business-faqs/${id}`, { method: "DELETE" }),
    reorderFaqs: (orderedIds: number[]) =>
      fetchJSON<BusinessFaq[]>("/api/business-faqs/reorder", {
        method: "POST",
        body: JSON.stringify({ orderedIds }),
      }),
    cleanupFaq: (data: { question: string; answer: string; previous?: string[] }) =>
      fetchJSON<{ answer: string; options: string[] }>("/api/business-faqs/cleanup", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    cleanupContent: (data: { kind: string; text: string; title?: string; previous?: string[] }) =>
      fetchJSON<{ text: string; options: string[] }>("/api/website-content/cleanup", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  },
  // Digital Receptionist knowledgebase (Marketing → Digital Receptionist tab).
  // Anonymous receptionist conversations (Task 1704): finished chats where
  // the visitor never left contact details. Dismiss = permanent delete.
  receptionistConversations: {
    list: () => fetchJSON<ReceptionistConversation[]>("/api/receptionist-conversations"),
    forLead: (leadId: number) =>
      fetchJSON<{ receptionistName: string; conversations: ReceptionistConversation[] }>(
        `/api/leads/${leadId}/receptionist-conversations`,
      ),
    dismiss: (id: number) =>
      fetchJSON<{ success: boolean }>(`/api/receptionist-conversations/${id}`, {
        method: "DELETE",
      }),
  },
  receptionistTextAlertHistory: {
    list: (offset = 0, limit = 20) =>
      fetchJSON<ReceptionistTextAlertHistoryPage>(
        `/api/receptionist/text-alert-history?${new URLSearchParams({
          offset: String(offset),
          limit: String(limit),
        })}`,
      ),
  },
  // Per-question answer-gap dismissals (Task 1733): "don't ask me about this
  // again" for flagged questions the owner won't answer in chat. The server
  // computes the normalized key from the question text.
  receptionistGapDismissals: {
    list: () => fetchJSON<ReceptionistGapDismissal[]>("/api/receptionist-gap-dismissals"),
    create: (question: string) =>
      fetchJSON<{ success: boolean; questionKey: string }>("/api/receptionist-gap-dismissals", {
        method: "POST",
        body: JSON.stringify({ question }),
      }),
  },
  // Answer gaps (Task 1732): server-side scan of ALL done conversations —
  // lead-captured included — returning only the flagged question/reply pairs.
  receptionistGaps: {
    list: () => fetchJSON<AnswerGap[]>("/api/receptionist-gaps"),
  },
  receptionistKb: {
    list: () => fetchJSON<ReceptionistKbEntry[]>("/api/receptionist-kb"),
    create: (data: { question?: string; answer?: string; sourceCampaignId?: number }) =>
      fetchJSON<ReceptionistKbEntry>("/api/receptionist-kb", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (
      id: number,
      data: { question?: string; answer?: string; sourceCampaignId?: number | null },
    ) =>
      fetchJSON<ReceptionistKbEntry>(`/api/receptionist-kb/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    approve: (id: number) =>
      fetchJSON<ReceptionistKbEntry>(`/api/receptionist-kb/${id}/approve`, {
        method: "POST",
      }),
    remove: (id: number) =>
      fetchJSON<{ success: boolean }>(`/api/receptionist-kb/${id}`, { method: "DELETE" }),
    // AI-drafted combined Q&A for two flagged duplicates. Draft only — the
    // owner reviews/edits in the merge editor before anything is saved.
    mergeDraft: (idA: number, idB: number) =>
      fetchJSON<{ question: string; answer: string }>(
        "/api/receptionist-kb/merge-draft",
        { method: "POST", body: JSON.stringify({ idA, idB }) },
      ),
    confirm: () =>
      fetchJSON<{ receptionistKbConfirmedAt: string | null }>(
        "/api/receptionist-kb/confirm",
        { method: "POST" },
      ),
    // Idempotent early seed (onboarding Receptionist step): pre-populates the
    // PENDING starter questions from the profile data saved so far. No-op for
    // users who already have any KB rows.
    seed: () =>
      fetchJSON<{ created: number }>("/api/receptionist-kb/seed", { method: "POST" }),
    fromSlide: (slideId: number) =>
      fetchJSON<ReceptionistKbEntry>("/api/receptionist-kb/from-slide", {
        method: "POST",
        body: JSON.stringify({ slideId }),
      }),
    // Re-draft a slide-sourced entry from the CURRENT slide text + attached
    // video script. 409 when the owner has edited the entry and confirm isn't
    // set — re-call with confirm:true after the owner agrees to replace it.
    refreshFromSlide: (id: number, opts?: { confirm?: boolean }) =>
      fetchJSON<{ entry: ReceptionistKbEntry; changed: boolean }>(
        `/api/receptionist-kb/${id}/refresh-from-slide`,
        { method: "POST", body: JSON.stringify(opts?.confirm ? { confirm: true } : {}) },
      ),
    // Slide-sourced entries whose slide has changed since the entry was
    // drafted/refreshed — shown as "Out of Date with Its Slide" on the cards.
    stale: () =>
      fetchJSON<{ staleIds: number[] }>("/api/receptionist-kb/stale"),
    // One-click bulk refresh: untouched stale entries refresh silently;
    // owner-edited ones are skipped (they keep the per-card confirm flow).
    refreshAllStale: () =>
      fetchJSON<{ refreshed: number; skippedEdited: number }>(
        "/api/receptionist-kb/refresh-all-stale",
        { method: "POST", body: JSON.stringify({}) },
      ),
    // "These Look Similar" review flow: near-duplicate pairs the owner should
    // verify. Resolve keeps one entry (deletes the other); dismiss = Keep Both
    // (records the pair so it is never re-flagged).
    conflicts: () =>
      fetchJSON<{ pairs: Array<{ a: ReceptionistKbEntry; b: ReceptionistKbEntry; score: number }> }>(
        "/api/receptionist-kb/conflicts",
      ),
    resolveConflict: (keepId: number, removeId: number) =>
      fetchJSON<{ success: boolean; kept: ReceptionistKbEntry }>(
        "/api/receptionist-kb/conflicts/resolve",
        { method: "POST", body: JSON.stringify({ keepId, removeId }) },
      ),
    dismissConflict: (aId: number, bId: number) =>
      fetchJSON<{ success: boolean }>("/api/receptionist-kb/conflicts/dismiss", {
        method: "POST",
        body: JSON.stringify({ aId, bId }),
      }),
    // "Edit & Merge": the owner composed one merged Q&A during conflict
    // review — it lands on the kept entry and the other entry is removed.
    mergeConflict: (data: {
      keepId: number;
      removeId: number;
      question: string;
      answer: string;
    }) =>
      fetchJSON<{ success: boolean; kept: ReceptionistKbEntry }>(
        "/api/receptionist-kb/conflicts/merge",
        { method: "POST", body: JSON.stringify(data) },
      ),
  },
  customDomain: {
    get: () => fetchJSON<CustomDomainResponse>("/api/custom-domain"),
    set: (domain: string) =>
      fetchJSON<CustomDomainResponse>("/api/custom-domain", {
        method: "PUT",
        body: JSON.stringify({ domain }),
      }),
    refresh: () =>
      fetchJSON<CustomDomainResponse>("/api/custom-domain/refresh", { method: "POST" }),
    remove: () =>
      fetchJSON<CustomDomainResponse>("/api/custom-domain", { method: "DELETE" }),
  },
  businessLegal: {
    get: (kind: LegalKind) => fetchJSON<LegalDocResponse>(`/api/business-legal/${kind}`),
    update: (kind: LegalKind, sections: LegalSection[]) =>
      fetchJSON<LegalDocResponse>(`/api/business-legal/${kind}`, {
        method: "PATCH",
        body: JSON.stringify({ sections }),
      }),
    reset: (kind: LegalKind) =>
      fetchJSON<LegalDocResponse>(`/api/business-legal/${kind}`, { method: "DELETE" }),
  },
  platformLegal: {
    get: (kind: LegalKind) =>
      fetchJSON<PlatformLegalDocResponse>(`/api/platform-legal/${kind}`),
    update: (kind: LegalKind, sections: LegalSection[]) =>
      fetchJSON<PlatformLegalDocResponse>(`/api/platform-legal/${kind}`, {
        method: "PATCH",
        body: JSON.stringify({ sections }),
      }),
    reset: (kind: LegalKind) =>
      fetchJSON<PlatformLegalDocResponse>(`/api/platform-legal/${kind}`, {
        method: "DELETE",
      }),
  },
  businessSeo: {
    crawlerUrls: () => fetchJSON<{
      websiteUrl: string | null;
      robotsUrl: string | null;
      sitemapUrl: string | null;
      sharedHost: boolean;
    }>("/api/business-seo/crawler-urls"),
    get: () => fetchJSON<SeoResponse>("/api/business-seo"),
    update: (data: SeoMeta) =>
      fetchJSON<SeoResponse>("/api/business-seo", {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
  },
  businessSlides: {
    ...contentApi<
      BusinessSlide & BusinessSlideActionFields,
      InsertBusinessSlide & Partial<BusinessSlideActionFields>
    >("business-slides"),
    // Owner contact tag on a library photo (Task 2142) — the ONE sanctioned
    // client write path for a slide's leadId (the generic PATCH strips it).
    // null clears the tag; the server ownership-validates the lead.
    setLead: (id: number, leadId: number | null) =>
      fetchJSON<BusinessSlide>(`/api/business-slides/${id}/lead`, {
        method: "PATCH",
        body: JSON.stringify({ leadId }),
      }),
  },
  getPricing: {
    get: () =>
      fetchJSON<{
        tab: WebsiteTab | null;
        slide: BusinessSlide | null;
        showOnWebsite: boolean;
      }>("/api/get-pricing"),
    createSlide: () =>
      fetchJSON<{ slide: BusinessSlide }>("/api/get-pricing/slide", {
        method: "POST",
      }),
  },
  websiteTabs: {
    ...contentApi<WebsiteTab, InsertWebsiteTab>("website-tabs"),
    deleteWithSlides: (input: {
      key: string;
      slides: "delete" | "move";
      destinationKey?: string;
      expectedSlideCount: number;
    }) =>
      fetchJSON<{ success: boolean; affectedSlides: number }>("/api/website-tabs/delete", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    // Full interleaved nav order — user tabs ("tab-<id>") plus the dynamic
    // built-in keys ("current-offers" / "client-feedback" / "sessions" / "blog").
    // Always send the complete list; the server persists their positions.
    reorderNav: (orderedKeys: string[]) =>
      fetchJSON<{ orderedKeys: string[] }>("/api/website-tabs/nav-order", {
        method: "POST",
        body: JSON.stringify({ orderedKeys }),
      }),
  },
  // Per-URL custom media names — the "Choose From My Media" picker's rename
  // affordance. One optional name per media url; a blank name clears it so the
  // caption falls back to slide-title/area/job-label sources. Never touches
  // slide titles or generation-job labels.
  mediaNames: {
    list: () => fetchJSON<{ names: Record<string, string> }>("/api/media-names"),
    set: (mediaUrl: string, name: string) =>
      fetchJSON<{ names: Record<string, string> }>("/api/media-names", {
        method: "PUT",
        body: JSON.stringify({ mediaUrl, name }),
      }),
  },
  // Video poster frames (thumbnail stills) — real extracted frames pinned per
  // video url so resting video cards never show a mid-blink frame. Manual
  // picks (Edit Thumbnail scrubber) always win over auto.
  videoPosters: {
    list: () =>
      fetchJSON<{ posters: Record<string, { posterUrl: string; posterTime: number; source: "auto" | "manual" }> }>(
        "/api/video-posters",
      ),
    auto: (videoUrl: string, force = true) =>
      fetchJSON<{ posterUrl: string; posterTime: number; source: "auto" | "manual" }>("/api/video-posters/auto", {
        method: "POST",
        body: JSON.stringify({ videoUrl, force }),
      }),
    setManual: (videoUrl: string, timeSec: number) =>
      fetchJSON<{ posterUrl: string; posterTime: number; source: "auto" | "manual" }>("/api/video-posters", {
        method: "PUT",
        body: JSON.stringify({ videoUrl, timeSec }),
      }),
  },
  media: {
    // Upload a file to object storage (Bunny CDN) and get back a public HTTPS
    // URL. Sends the raw file bytes (not base64/JSON) so large images don't
    // bloat the request. `kind` namespaces the stored path. For kind "logo"
    // the server also best-effort extracts a dominant brand color from the
    // image (null when it can't) so the caller can suggest an accent color.
    upload: async (
      file: File,
      kind: "logo" | "media" | "voice" = "media",
      context?: "website-slide",
    ): Promise<{ url: string; dominantColor?: string | null }> => {
      const headers: Record<string, string> = {
        "Content-Type": file.type || "application/octet-stream",
        // Original filename (URI-encoded — header values must be ASCII) so the
        // server can register a friendly Media Library name for logo uploads.
        "X-File-Name": encodeURIComponent(file.name || ""),
        ...(await authHeader()),
      };
      const query = new URLSearchParams({ kind });
      if (context) query.set("context", context);
      const res = await fetch(`/api/media/upload?${query.toString()}`, {
        method: "POST",
        headers,
        credentials: "include",
        body: file,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        // Carry the HTTP status so callers can tell a quality rejection (422 —
        // e.g. a voice sample that's too quiet/short/clipped, fixed only by
        // re-recording) apart from a transient failure worth retrying.
        const err = new Error(body.error || res.statusText) as Error & { status?: number };
        err.status = res.status;
        throw err;
      }
      return res.json();
    },
    // Take an already-uploaded image (its CDN url), remove its background, and
    // composite the subject onto the fixed standard dark studio plate. Returns
    // a new CDN url; the original is left intact.
    // `cutoutUrl` (optional): a pre-computed transparent cut-out (from
    // `studioCutout`). When supplied, the server skips background removal and
    // composites straight onto the plate so the heavy model runs once per photo.
    // `backgroundUrl` (optional): an owner-uploaded background still (its CDN
    // url); when supplied the subject is placed on THAT image instead of the
    // fixed studio plate.
    studioComposite: async (
      url: string,
      cutoutUrl?: string,
      backgroundUrl?: string,
      variant?: "charcoal" | "white" | "accent",
    ): Promise<{ url: string }> => {
      const res = await fetch("/api/media/studio-composite", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeader()) },
        credentials: "include",
        body: JSON.stringify({
          url,
          ...(cutoutUrl ? { cutoutUrl } : {}),
          ...(backgroundUrl ? { backgroundUrl } : {}),
          ...(variant ? { variant } : {}),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(body.error || res.statusText);
      }
      return res.json();
    },
    // Regenerate the per-user accent professional plate to match the current
    // brand accent (Task #1417). Never touches already-created images/videos —
    // only the stored accent plate used for NEW composites. Returns the fresh
    // plate url (or "" when media storage isn't configured).
    regenerateAccentBackground: async (): Promise<{ url: string }> => {
      const res = await fetch("/api/business-profile/accent-background/regenerate", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeader()) },
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(body.error || res.statusText);
      }
      return res.json();
    },
    // Logo helpers (Branding tab + onboarding). Each takes an already-uploaded
    // logo's CDN url and returns a NEW derived PNG's url; the original object
    // stays untouched so the caller can revert.
    logoRemoveBackground: async (url: string): Promise<{ url: string }> => {
      const res = await fetch("/api/media/logo-remove-background", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeader()) },
        credentials: "include",
        body: JSON.stringify({ url }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(body.error || res.statusText);
      }
      return res.json();
    },
    // Single "Upload Your Logo" role sorter (Task #943). Give it the already-
    // uploaded logo file url(s) (in the order picked); it auto-sorts them into
    // the three rendered roles, derives whatever's missing (square icon +
    // dark-background knockout), and returns the role urls + an accent
    // suggestion. `overrides` forces a specific uploaded file into a role
    // (the preview's "use a different file here" control). A blank wideLogoUrl
    // means no good dark version could be made → the text fallback is used.
    logoRoles: async (
      urls: string[],
      overrides?: { icon?: string; primary?: string; dark?: string },
    ): Promise<{
      logoUrl: string;
      logoIconUrl: string;
      wideLogoUrl: string;
      dominantColor?: string | null;
      suggestedDark?: string | null;
      suggestedLight?: string | null;
    }> => {
      const res = await fetch("/api/media/logo-roles", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeader()) },
        credentials: "include",
        body: JSON.stringify(overrides ? { urls, overrides } : { urls }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(body.error || res.statusText);
      }
      return res.json();
    },
    // Recolor the logo's visible pixels to white/light (alpha-masked) for use
    // on the black site header. Multi-color logos flatten to one light color.
    logoLighten: async (url: string): Promise<{ url: string }> => {
      const res = await fetch("/api/media/logo-lighten", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeader()) },
        credentials: "include",
        body: JSON.stringify({ url }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(body.error || res.statusText);
      }
      return res.json();
    },
    // Strip the background from an already-uploaded image and return the
    // transparent cut-out's CDN url. Used to drive the instant local preview
    // (cut-out over a CSS gradient) before committing the composite.
    studioCutout: async (url: string): Promise<{ url: string }> => {
      const res = await fetch("/api/media/studio-cutout", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeader()) },
        credentials: "include",
        body: JSON.stringify({ url }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(body.error || res.statusText);
      }
      return res.json();
    },
    // Best-effort: ask the server to reclaim a studio composite the UI just
    // replaced or discarded so storage is freed right away instead of waiting
    // for the periodic orphan sweep. The server only deletes it when it's no
    // longer referenced, so this never throws and never removes a live image.
    reclaimComposite: async (url: string): Promise<{ reclaimed: boolean }> => {
      try {
        const res = await fetch("/api/media/studio-reclaim", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(await authHeader()) },
          credentials: "include",
          body: JSON.stringify({ url }),
        });
        if (!res.ok) return { reclaimed: false };
        return await res.json().catch(() => ({ reclaimed: false }));
      } catch {
        return { reclaimed: false };
      }
    },
  },
};

function buildRangeQuery(range?: { from?: string; to?: string }): string {
  if (!range) return "";
  const params = new URLSearchParams();
  if (range.from) params.set("from", range.from);
  if (range.to) params.set("to", range.to);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

async function downloadBlob(url: string): Promise<{ blob: Blob; filename: string | null }> {
  const headers: Record<string, string> = { ...(await authHeader()) };
  const res = await fetch(url, { headers, credentials: "include" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || res.statusText);
  }
  const disposition = res.headers.get("content-disposition") || "";
  const match = /filename="?([^"]+)"?/i.exec(disposition);
  const filename = match ? match[1] : null;
  const blob = await res.blob();
  return { blob, filename };
}

export type ImportMatchField = "email" | "phone" | "name" | "website" | "similar-name";

export type ImportDuplicateMatch = {
  index: number;
  matchedField: ImportMatchField;
  existingContact: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    website: string | null;
    company: string | null;
  };
};

export type DuplicateGroupReason = "email" | "phone" | "name" | "similar-name";
export type DuplicateGroup = {
  ids: number[];
  reason: DuplicateGroupReason;
};

export type DismissedDuplicateGroup = {
  signature: string;
  ids: number[];
  createdAt: string;
  contacts: { id: number; firstName: string | null; lastName: string | null; email: string | null }[];
};

// Admin-managed playbook category (global grouping for playbooks).
// playbookCount drives the UI's delete guard (server 409s on non-empty).
export type PlaybookCategory = {
  id: number;
  name: string;
  sortOrder: number;
  createdAt: string;
  playbookCount: number;
};

// Admin-authored Offer Template / Playbook (full admin shape — mirrors
// offer_templates). Overview fields + the Leads & Follow-Up mirror copy onto
// the user's campaign at activation ({{businessName}} merged in text).
// Referral program (Task 1806): admin Referrals tab payload.
export type AdminReferralReferrerRow = {
  userId: string;
  name: string | null;
  isAdmin: boolean;
  referralCode: string | null;
  clicks: number;
  signups: number;
  paying: number;
  signupConversion: number | null;
  closeRate: number | null;
};

export type AdminReferralLedgerRow = {
  id: number;
  referrerUserId: string;
  referrerName: string | null;
  referredUserId: string;
  referredName: string | null;
  amountCents: number;
  status: "pending" | "applied" | "voided";
  firstPaidAt: string | null;
  eligibleAt: string | null;
  appliedAt: string | null;
  voidedAt: string | null;
  voidReason: string;
  createdAt: string | null;
};

// Demo studio background reseed status (Task 2172). Mirrors DemoReseedStatus
// in server/demoStudioSeed.ts.
export type DemoReseedStatus = {
  running: boolean;
  runId: number;
  /** Latest step message while running (Task 2178); null when idle. */
  currentStep: string | null;
  lastRun:
    | { ok: true; finishedAt: string; result: { demoUserId: string; created: boolean; checkins: number; rsvps: number; warnings: string[] } }
    | { ok: false; finishedAt: string; error: string }
    | null;
};

// Dev-era stray accounts scan (Task 1861). Mirrors DevEraStrayScan in server/seed.ts.
export type DevEraStrayScan = {
  checkedAt: string;
  profileCount: number;
  strayIds: string[];
  adminManagedDemoIds?: string[];
  errors: string[];
};

export type AdminRewardEmailResponse = {
  overrides: { subject: string; body: string };
  defaults: { subject: string; body: string };
  preview: { subject: string; body: string };
  amountCents: number;
  limits: { subjectMax: number; bodyMax: number };
};

export type AdminReferralsResponse = {
  referrers: AdminReferralReferrerRow[];
  ledger: AdminReferralLedgerRow[];
  totals: {
    clicks: number;
    signups: number;
    paying: number;
    pendingCents: number;
    appliedCents: number;
    voidedCents: number;
  };
};

// ── Affiliate program (Task 2006) ────────────────────────────────────────────
export type AdminAffiliateReferralRow = {
  id: number;
  referredUserId: string;
  referredName: string | null;
  firstPaidAt: string;
  eligibleAt: string;
  status: "qualifying" | "active" | "voided";
  voidedAt: string | null;
  voidReason: string;
  subscriptionStatus: string | null;
};

export type AffiliatePaymentRecoveryWarningInput = {
  affiliateId: number;
  paymentId: number;
  message: string;
  createdAt: string;
};

export type AffiliatePaymentRecoveryWarning = AffiliatePaymentRecoveryWarningInput & {
  id: number;
  operatorUserId: string;
  updatedAt: string;
};

export type AdminAffiliateRow = {
  id: number;
  userId: string | null;
  isCustomer: boolean;
  name: string | null;
  email: string | null;
  paypalEmail: string | null;
  code: string;
  link: string;
  status: "pending" | "active" | "disabled";
  approvedAt: string | null;
  // Task 2028: set when the affiliate actually joined the program (opt-in).
  // Null = dormant auto-enrolled row that can't attribute or earn.
  optedInAt: string | null;
  createdAt: string;
  accruedCents: number;
  paidCents: number;
  balanceCents: number;
  unmatchedAccrualCount: number;
  referrals: AdminAffiliateReferralRow[];
  accruals: {
    id: number;
    referredUserId: string;
    referredName: string | null;
    month: string;
    amountCents: number;
    accessPeriod: {
      periodStart: string;
      periodEnd: string;
      stripeSubscriptionId: string;
    } | null;
    createdAt: string;
  }[];
  payments: {
    id: number;
    amountCents: number;
    paidAt: string;
    paypalTxnId: string;
    note: string;
    createdAt: string;
  }[];
};

export type AdminAffiliatesResponse = {
  affiliates: AdminAffiliateRow[];
  totals: {
    accruedCents: number;
    paidCents: number;
    balanceCents: number;
    unmatchedAccrualCount: number;
    pendingApprovals: number;
  };
  diagnostics: {
    payoutConflicts: {
      affiliateId: number;
      affiliateName: string | null;
      count: number;
      firstOccurredAt: string;
      lastOccurredAt: string;
    }[];
  };
};

// Customer affiliate opt-in (Task 2024): null until the user has joined.
export type AffiliateMeResponse = {
  affiliate: {
    status: string;
    code: string;
    link: string;
    paypalEmail: string;
  } | null;
};

export type OfferTemplate = {
  id: number;
  name: string;
  shared: boolean;
  pricingMessage: string;
  ctaLabel: string;
  confirmationMessage: string;
  confirmationMode?: "message" | "page";
  confirmationUrl?: string;
  afterCtaLabel: string;
  afterCtaUrl: string;
  offerScript: string;
  afterScript: string;
  audience: string;
  priceText: string;
  scheduleText: string;
  locationText: string;
  tone: string;
  // Overview (playbook guidance)
  categoryId: number | null;
  purpose: string;
  bestFor: string;
  avoidIf: string;
  estimatedSetup: string;
  successTips: string;
  whyThisWorks: string;
  // Leads & Follow-Up mirror (same names as the campaign fields)
  requireFirstName: boolean;
  requireLastName: boolean;
  requirePhone: boolean;
  firstNameRequired: boolean;
  lastNameRequired: boolean;
  phoneRequired: boolean;
  requireAddressLine1: boolean;
  requireAddressLine2: boolean;
  requireCity: boolean;
  requireRegion: boolean;
  requirePostalCode: boolean;
  addressLine1Required: boolean;
  addressLine2Required: boolean;
  cityRequired: boolean;
  regionRequired: boolean;
  postalCodeRequired: boolean;
  followUpEmailEnabled: boolean;
  followUpEmailSubject: string;
  followUpEmailBody: string;
  followUpEmailSignatureEnabled: boolean;
  autoCreateFollowUpTask: boolean;
  followUpTitle: string;
  followUpDaysOut: number;
  addToPipeline: boolean;
  followUpLeadTagEnabled: boolean;
  relationshipTags: string[];
  // Marketing Assets mirror (Task 1763) — master promo copy, personalized +
  // copied onto the user's campaign at activation.
  assetFacebookPost: string;
  assetLinkedinPost: string;
  assetListEmailSubject: string;
  assetListEmailBody: string;
  assetFollowUpEmailSubject: string;
  assetFollowUpEmailBody: string;
  // Branded graphic (Task 1792): "" = playbook ships no graphic.
  graphicTemplateKey: string;
  graphicHeadline: string;
  graphicSubline: string;
  graphicBadge: string;
  graphicBody: string;
  // Consult message library master (Task 1794): both confirmation variants +
  // reminder/fu1-3/close-out. null = playbook ships no library.
  consultLibrary: TemplateConsultLibrary | null;
  // Consultation FAQs seeded into the installer's receptionist KB (pending).
  kbFaqs: ConsultKbFaq[] | null;
  createdAt: string;
  updatedAt: string;
};
export type OfferTemplateInput = Omit<OfferTemplate, "id" | "createdAt" | "updatedAt">;

// ── Branded campaign graphic (Task 1792) ────────────────────────────────────
export type CampaignGraphic = {
  id: number;
  campaignId: number;
  templateKey: string;
  headline: string;
  subline: string;
  badge: string;
  body: string;
  primaryColor: string;
  secondaryColor: string;
  photoUrl: string;
  photoFocalX: number;
  photoFocalY: number;
  imageUrl: string;
  createdAt: string;
  updatedAt: string;
};
export type CampaignGraphicSaveInput = Omit<CampaignGraphic, "id" | "campaignId" | "imageUrl" | "createdAt" | "updatedAt">;

// ── Playbook Library (user-facing catalog of Lynn's shared playbooks) ──
export type PlaybookCategoryTile = {
  id: number;
  name: string;
  playbookCount: number;
};
// Trimmed library payload: Overview guidance + a read-only "what you get"
// summary. Never the raw presenter scripts or admin draft rows.
export type PlaybookSummary = {
  id: number;
  name: string;
  categoryId: number | null;
  purpose: string;
  bestFor: string;
  avoidIf: string;
  estimatedSetup: string;
  successTips: string;
  whyThisWorks: string;
  pricingMessage: string;
  ctaLabel: string;
  confirmationMessage: string;
  confirmationMode?: "message" | "page";
  confirmationUrl?: string;
  afterCtaLabel: string;
  hasOfferVideo: boolean;
  followUpEmailEnabled: boolean;
  autoCreateFollowUpTask: boolean;
  followUpDaysOut: number;
  addToPipeline: boolean;
  // "What You Get" flags — true only when the playbook actually carries the
  // piece: ready-to-use promo copy, the consult message journey, receptionist
  // KB FAQs, and a branded promo graphic template.
  hasMarketingAssets: boolean;
  hasConsultLibrary: boolean;
  hasKbFaqs: boolean;
  hasGraphic: boolean;
  // True when one of this user's campaigns was installed from this playbook
  // (library install or the onboarding "Your First Offer" step).
  installed: boolean;
};
export type PlaybookLibraryResponse = {
  categories: PlaybookCategoryTile[];
  uncategorizedCount: number;
  // Which category is "Customer Growth" (server-decided, like playbook-groups)
  // — the Reactivate Past Customers entry nests inside its view.
  growthCategoryId: number | null;
  playbooks: PlaybookSummary[];
};
// One requested presenter clip from an install: either its render started
// (poll /api/ai-video for the job) or it was skipped with a reason.
export type PlaybookInstallVideo =
  | { jobId: number; status: "started" }
  | { jobId: null; status: "skipped"; reason: "no-script" | "no-presenter" | "monthly-cap" | "library-full" };
export type PlaybookInstallResult = {
  campaign: CaptureLayerWidget;
  videos: { offer: PlaybookInstallVideo; reel: PlaybookInstallVideo };
};

export type CaptureLayerWidget = {
  id: number;
  slug: string;
  assignedUserId: string;
  name: string;
  campaignType: "pricing";
  // Offer-template provenance (playbook installs stamp this; hand-created
  // campaigns carry null). Used to detect consultation-flow contacts.
  sourceOfferTemplateId: number | null;
  accentColor: string;
  iconColor: string;
  iconColorMode: "auto" | "manual";
  headerTitle: string;
  businessName: string;
  cardBadge: string;
  cardIntro: string;
  cardPrimaryLabel: string;
  // What the hero primary ("Send a Message") button does on the public page:
  // "message" runs the inline lead-capture flow; "contact" links to the
  // public Contact tab/form.
  heroPrimaryCtaTarget: "message" | "contact";
  profileLogoUrl: string;
  // Derived SQUARE icon used as the public page's favicon (falls back to the
  // primary logo server-side). Crisp at 16px where the wide logo squishes.
  logoIconUrl: string;
  heroIntroVideoUrl: string;
  heroPricingVideoUrl: string;
  heroMessageVideoUrl: string;
  // How the Offer IMAGE sits in the public slide's 16:9 frame: "fit"
  // letterboxes over the charcoal plate (default), "fill" crops edge-to-edge.
  // Videos always fit — the chooser only shows for images.
  mediaFit: "fit" | "fill";
  // Focal position (percent, 0–100) of the fill-mode Offer-image crop — CSS
  // object-position. 50/50 = centered (default; how every image cropped
  // before the feature existed).
  mediaFocalX: number;
  mediaFocalY: number;
  // Zoom (percent, 100–300) of the fill-mode crop; 100 = no zoom.
  mediaZoom: number;
  // "After They Submit" media (image or video) shown at the top of the slide's
  // done phase. Empty = keep the offer media. (Reuses the historical
  // hero_reveal_video_url column — name is legacy, content may be image/video.)
  heroRevealVideoUrl: string;
  // Optional done-phase CTA button under the confirmation text. Label empty =
  // no button; afterCtaUrl is an external URL or internal "slide:"/"campaign:"
  // token (see vault-template/internalNav.ts).
  afterCtaLabel?: string;
  afterCtaUrl?: string;
  // Per-campaign presenter scripts. offerScript = the Offer video (plays while
  // the form is filled); saved draft, seeds the video generator's default
  // script (empty = generic suggested script). afterScript is DORMANT
  // (Thank-You Media retired July 2026) — carried so PATCHes never drop it.
  offerScript: string;
  afterScript: string;
  // Guided "Current Special" brief — raw owner answers feeding the AI drafts
  // (re-editable when the owner reopens the special).
  audience: string;
  priceText: string;
  scheduleText: string;
  locationText: string;
  tone: string;
  requireFirstName: boolean;
  requireLastName: boolean;
  requirePhone: boolean;
  firstNameRequired: boolean;
  lastNameRequired: boolean;
  phoneRequired: boolean;
  // Per-address-field REQUIRED flags (this campaign's own). Enforced only when
  // the matching show flag below is also on. Drives the hero + Current Specials.
  addressLine1Required: boolean;
  addressLine2Required: boolean;
  cityRequired: boolean;
  regionRequired: boolean;
  postalCodeRequired: boolean;
  requireAddressLine1: boolean;
  requireAddressLine2: boolean;
  requireCity: boolean;
  requireRegion: boolean;
  requirePostalCode: boolean;
  defaultPipelineStageSlug: string | null;
  addToPipeline: boolean;
  // The Channel every lead captured through this campaign is tagged with.
  // Campaign-driven (message → "IC Receptionist", pricing → "IC Pricing"),
  // set server-side from the campaign type — read-only on the client.
  channel: string;
  notifyOnLead: boolean;
  notificationEmail: string | null;
  // Per-campaign follow-up email to the submitter (subject + body, on/off).
  followUpEmailEnabled: boolean;
  followUpEmailSubject: string;
  followUpEmailBody: string;
  // Append the owner's email signature (Settings → Email Signature) to the
  // follow-up email. Only has an effect when a signature is configured.
  followUpEmailSignatureEnabled: boolean;
  // Optional image at the top of the follow-up email ("" = none). Must be
  // the owner's logo or a media-library image (ownership-checked server-side).
  followUpEmailHeaderImageUrl: string;
  // Per-campaign Relationship tagging control. Source/Direction/Channel stay
  // locked; the owner may drop the default "Lead" tag and add extra tags.
  followUpLeadTagEnabled: boolean;
  relationshipTags: string[];
  autoCreateFollowUpTask: boolean;
  followUpTitle: string;
  followUpDaysOut: number;
  followUpFolder: string;
  confirmationMessage: string;
  confirmationMode?: "message" | "page";
  confirmationUrl?: string;
  // Marketing Assets (Task 1763) — ready-to-use promo copy, AI-personalized
  // at playbook install. Social posts are copy-to-clipboard; the list email
  // pre-loads the compose modal; the follow-up email/SMS pre-fill the per-lead
  // senders on the contact card. Empty string = no asset.
  assetFacebookPost: string;
  assetLinkedinPost: string;
  assetListEmailSubject: string;
  assetListEmailBody: string;
  // Saved header image for the list email (Task 1814); "" = none. Must be
  // the owner's logo or a media-library image (ownership-checked server-side).
  assetListEmailHeaderImageUrl: string;
  assetFollowUpEmailSubject: string;
  assetFollowUpEmailBody: string;
  // Consult message library (Task 1794): the installed, personalized
  // consultation message set. null = not a v2 playbook install — consult-task
  // panels fall back to the generic shared/consultSteps.ts drafts.
  consultLibrary: CampaignConsultLibrary | null;
  // Image attachments shown with the campaign's Our Specials slide (user-owned
  // CDN URLs — copies are made at generate time for template images).
  imageUrls: string[];
  // On/Off: whether this campaign's slide shows on the public Our Specials
  // tab. Campaigns toggle independently (any number On, including zero).
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CaptureLayerWidgetUpdate = Omit<
  CaptureLayerWidget,
  "id" | "slug" | "assignedUserId" | "createdAt" | "updatedAt"
>;

export type CaptureLayerChip = {
  id: number;
  widgetId: number;
  question: string;
  answer: string;
  ctaType: "none" | "message" | "schedule" | "url";
  ctaLabel: string | null;
  ctaTargetUrl: string | null;
  sortOrder: number;
  createdAt: string;
};

export type CaptureLayerChipInput = {
  question: string;
  answer: string;
  ctaType?: "none" | "message" | "schedule" | "url";
  ctaLabel?: string | null;
  ctaTargetUrl?: string | null;
};

export type OutreachMasterSummary = {
  key: "referral_request" | "review_request";
  label: string;
};

export type AdminReactivationMaster = {
  key: ReactivationGoalKey;
  label: string;
  title: string;
  defaults: Record<ReactivationField, string>;
  overrides: Partial<Record<ReactivationField, string>>;
};

export type AdminOutreachMaster = {
  key: OutreachMasterKey;
  label: string;
  templateName: string;
  defaults: Record<OutreachField, string>;
  overrides: Partial<Record<OutreachField, string>>;
};

export type ClientGoalCheckInsResponse = {
  staleDays: number;
  items: ClientGoalCheckInEntry[];
};

export type CreateImportantDateInput = {
  month: number;
  day: number;
  year?: number | null;
  label: string;
  reminderEnabled?: boolean;
  reminderDaysBefore?: number | null;
  recurrenceUnit?: "day" | "week" | "month" | "year" | null;
  recurrenceInterval?: number | null;
};

export type ScheduledEmail = {
  id: number;
  scheduledAt: string;
  timezone: string;
  subject: string;
  body: string;
  fromName: string;
  status: "pending" | "processing" | "failed" | "canceled";
  statusDetail: string;
  recipientCount: number;
  scheduledRecipients?: number;
  sentRecipients?: number;
  failedRecipients?: number;
  skippedRecipients?: number;
  diagnostic?: EmailDiagnostic | null;
  lifecycle?: EmailLifecycleTransition[];
};

export type ScheduledEmailDetail = {
  batch: Omit<ScheduledEmail, "recipientCount"> & {
    signatureText: string;
    signatureHtml: string;
    attachments: { filename: string; content: string }[];
    headerImageUrl: string;
    headerAlign: "left" | "center" | "right";
    signatureAlign: "left" | "center" | "right";
    noShortenUrls: string[];
  };
  recipients: Array<{
    id: number;
    leadId: number;
    email: string;
    firstName: string;
    lastName: string;
    company: string;
    status: string;
    statusDetail?: string;
    diagnostic?: EmailDiagnostic | null;
  }>;
};
